import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { ExitAdjustmentFormat } from '../components/inventory/ExitAdjustmentFormat';
import html2pdf from 'html2pdf.js';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { 
  Package, Warehouse, ArrowRightLeft, TrendingDown, TrendingUp, 
  AlertTriangle, X, ChefHat, Wine, Users, Search, History,
  FileText, ArrowRight, BarChart3, ShoppingCart, DollarSign,
  Calendar, ClipboardList, RefreshCw, Bell, Mail, Circle, CheckCircle2, Filter, Plus, ShieldAlert, FileDown, FileSpreadsheet,
  ChevronRight, ChevronDown, Check, Pencil, Trash2, Printer, Eye, TrendingUp as TrendUp, AlertCircle
} from 'lucide-react';

const CHART_COLORS = ['#f97316', '#3b82f6', '#22c55e', '#a855f7', '#ef4444', '#14b8a6', '#f59e0b', '#6366f1'];

const AREA_ICONS = {
  'Almacén General': Warehouse,
  'Cocina': ChefHat,
  'Barra': Wine,
  'Meseros': Users
};

const Modal = ({ open, onClose, title, children, size = 'md' }) => {
  if (!open) return null;
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl'
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white text-slate-900 rounded-lg shadow-xl w-full ${sizeClasses[size]} mx-4 p-6 z-10 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
};

const Inventory = () => {
  const { api, user } = useAuth();
  const { branchId, branchName, isMatriz } = useBranch();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stock'); // stock, transfers, reports, notifications

  // Helper function to change tab without scroll jump
  const handleTabChange = (tabId) => {
    const scrollY = window.scrollY;
    setActiveTab(tabId);
    setTimeout(() => {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    }, 0);
  };
  
  const [areas, setAreas] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaStock, setAreaStock] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [processedSupplies, setProcessedSupplies] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // Transfer accordion state
  const [expandedBatches, setExpandedBatches] = useState({});
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [editTransferForm, setEditTransferForm] = useState({ quantity: '', reason: '', notes: '' });
  const [savingTransfer, setSavingTransfer] = useState(false);
  const [deletingTransfer, setDeletingTransfer] = useState(null);
  const isSubmittingTransfer = useRef(false); // Extra protection against double submit
  
  // Transfer filter dates
  const [transferDateFrom, setTransferDateFrom] = useState('');
  const [transferDateTo, setTransferDateTo] = useState('');
  
  // Transfer modal
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    from_area_id: '',
    to_area_id: '',
    supply_id: '',
    supply_search: '',
    quantity: '',
    reason: 'reabastecimiento',
    notes: '',
    to_branch_id: ''  // Sucursal destino para traspasos entre sucursales
  });
  const [valeFecha, setValeFecha] = useState(new Date().toISOString().slice(0, 10)); // YYYY-MM-DD format
  const [transferSourceStock, setTransferSourceStock] = useState([]);
  const [transferSourceProcessed, setTransferSourceProcessed] = useState([]); // Elaborados
  const [transferItems, setTransferItems] = useState([]); // Multiple items cart
  const [transferItemType, setTransferItemType] = useState('supply'); // supply or processed
  const [allBranches, setAllBranches] = useState([]); // Lista de sucursales para traspasos
  const [selectedTransfers, setSelectedTransfers] = useState({}); // Para seleccionar traspasos a imprimir
  
  // Dashboard panels state
  const [showValuePanel, setShowValuePanel] = useState(false);
  const [showAreaPanel, setShowAreaPanel] = useState(null); // 'from' or 'to' + area name
  const [showSupplyPanel, setShowSupplyPanel] = useState(null); // supply name
  const [showTimelinePanel, setShowTimelinePanel] = useState(false);
  const [showProductsDashboard, setShowProductsDashboard] = useState(false); // Panel completo de productos
  const [productsDashboardCategory, setProductsDashboardCategory] = useState(null); // Categoría expandida
  
  // Transfer filters and pagination
  const [transferFilterArea, setTransferFilterArea] = useState(''); // Filter by area
  const [transferFilterBranch, setTransferFilterBranch] = useState(''); // Filter by branch
  const [transferFilterSupply, setTransferFilterSupply] = useState(''); // Filter by supply name
  const [transferFilterDay, setTransferFilterDay] = useState(''); // Filter by day
  const [transferFilterMonth, setTransferFilterMonth] = useState(''); // Filter by month
  const [transferFilterYear, setTransferFilterYear] = useState(''); // Filter by year
  const [transfersPage, setTransfersPage] = useState(1);
  const [showTransferFilters, setShowTransferFilters] = useState(false); // Panel colapsable de filtros
  const TRANSFERS_PER_PAGE = 50;
  
  // Reports state
  const [reportType, setReportType] = useState('consumption'); // consumption, movements, purchases
  const [reportDays, setReportDays] = useState(30);
  const [reportPeriodType, setReportPeriodType] = useState('mes'); // hoy, semana, mes, todo, custom, month-specific
  const [reportSelectedMonth, setReportSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reportCustomStart, setReportCustomStart] = useState('');
  const [reportCustomEnd, setReportCustomEnd] = useState('');
  const [consumptionReport, setConsumptionReport] = useState(null);
  const [movementsReport, setMovementsReport] = useState([]);
  const [purchasesReport, setPurchasesReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [expandedMovementProducts, setExpandedMovementProducts] = useState({}); // Productos expandidos en movimientos
  const [movementFlowFilter, setMovementFlowFilter] = useState('all'); // all, within_matriz, to_branches, from_branches
  const [movementOriginBranch, setMovementOriginBranch] = useState('');
  const [movementDestBranch, setMovementDestBranch] = useState('');
  const [movementSearchTerm, setMovementSearchTerm] = useState(''); // Buscador de productos en movimientos  
  // Search and Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [printCatsOpen, setPrintCatsOpen] = useState(false);
  const [printCats, setPrintCats] = useState([]);
  const [areaSearchQuery, setAreaSearchQuery] = useState(''); // Search for areas/warehouses
  
  // Sync
  const [syncing, setSyncing] = useState(false);
  
  // Adjustment state
  const [adjustmentArea, setAdjustmentArea] = useState(null);
  const [adjustmentItems, setAdjustmentItems] = useState([]);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [adjustmentDate, setAdjustmentDate] = useState(new Date().toISOString().slice(0, 10)); // Custom date
  const [loadingAdjustment, setLoadingAdjustment] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [adjustmentSearch, setAdjustmentSearch] = useState(''); // Search for supplies in adjustment
  const [adjustmentSummary, setAdjustmentSummary] = useState(null);
  const adjustmentCacheRef = useRef({}); // Caché para datos de ajuste por área

  // Quick Adjustment Modal (Merma/Robo)
  const [quickAdjustModalOpen, setQuickAdjustModalOpen] = useState(false);
  const [quickAdjustItem, setQuickAdjustItem] = useState(null);
  const [quickAdjustForm, setQuickAdjustForm] = useState({
    direction: 'restar',
    adjustment_type: 'merma',
    quantity: '',
    reason: ''
  });

  const QUICK_ADJUST_TYPES = [
    { id: 'merma', name: 'Merma', color: 'text-orange-500', bgColor: 'bg-orange-500/20', description: 'Producto dañado', direction: 'restar' },
    { id: 'robo', name: 'Robo', color: 'text-red-500', bgColor: 'bg-red-500/20', description: 'Producto extraviado', direction: 'restar' },
    { id: 'vencido', name: 'Vencido', color: 'text-amber-500', bgColor: 'bg-amber-500/20', description: 'Producto caducado', direction: 'restar' },
    { id: 'ajuste', name: 'Ajuste', color: 'text-blue-500', bgColor: 'bg-blue-500/20', description: 'Corrección de conteo', direction: 'ambos' },
    { id: 'entrada', name: 'Entrada', color: 'text-green-600', bgColor: 'bg-green-500/20', description: 'Devolución / encontrado', direction: 'sumar' }
  ];

  // Helper: Obtener nombre completo de área con sucursal
  const getFullLocationName = (areaName, branchId) => {
    if (!branchId || !areaName) return areaName || '-';
    const branch = allBranches.find(b => b.branch_id === branchId);
    if (branch) {
      return `${areaName} (${branch.name})`;
    }
    return areaName;
  };

  // Helper: Calcular rango de fechas para reportes
  const getReportDateRange = () => {
    const now = new Date();
    let start, end, label;
    
    switch (reportPeriodType) {
      case 'hoy':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        label = `Hoy: ${start.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        break;
      case 'semana':
        const dayOfWeek = now.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        label = `Semana: ${start.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        break;
      case 'mes':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        label = `${start.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}`;
        break;
      case 'month-specific':
        const [year, month] = reportSelectedMonth.split('-').map(Number);
        start = new Date(year, month - 1, 1);
        end = new Date(year, month, 0, 23, 59, 59);
        label = `${start.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}`;
        break;
      case 'todo':
        start = new Date(2020, 0, 1);
        end = now;
        label = 'Todo el historial';
        break;
      case 'custom':
        start = reportCustomStart ? new Date(reportCustomStart) : new Date(2020, 0, 1);
        end = reportCustomEnd ? new Date(reportCustomEnd + 'T23:59:59') : now;
        label = `${start.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        label = 'Período actual';
    }
    
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return { start, end, days, label };
  };

  // Helper: Generar lista de últimos 12 meses
  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
      months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return months;
  };

  // Helper: Obtener info completa de origen/destino de un transfer
  const getTransferLocationInfo = (transfer) => {
    const fromBranch = transfer.from_branch_id ? allBranches.find(b => b.branch_id === transfer.from_branch_id) : null;
    const toBranch = transfer.to_branch_id ? allBranches.find(b => b.branch_id === transfer.to_branch_id) : null;
    
    return {
      fromFull: fromBranch ? `${transfer.from_area_name} (${fromBranch.name})` : transfer.from_area_name,
      toFull: toBranch ? `${transfer.to_area_name} (${toBranch.name})` : transfer.to_area_name,
      fromBranchName: fromBranch?.name || null,
      toBranchName: toBranch?.name || null
    };
  };

  // Helper: Filtrar y paginar traspasos
  const getFilteredTransfers = () => {
    let filtered = [...transfers];
    
    // Filtrar por área (origen o destino)
    if (transferFilterArea) {
      filtered = filtered.filter(t => 
        t.from_area_name === transferFilterArea || t.to_area_name === transferFilterArea
      );
    }
    
    // Filtrar por sucursal
    if (transferFilterBranch) {
      filtered = filtered.filter(t => 
        t.from_branch_id === transferFilterBranch || t.to_branch_id === transferFilterBranch
      );
    }
    
    // Filtrar por nombre de insumo
    if (transferFilterSupply) {
      const searchLower = transferFilterSupply.toLowerCase();
      filtered = filtered.filter(t => 
        (t.supply_name || '').toLowerCase().includes(searchLower)
      );
    }
    
    // Filtrar por fecha (día, mes, año)
    if (transferFilterYear || transferFilterMonth || transferFilterDay) {
      filtered = filtered.filter(t => {
        const date = new Date(t.created_at);
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // 1-12
        const day = date.getDate();
        
        if (transferFilterYear && year !== parseInt(transferFilterYear)) return false;
        if (transferFilterMonth && month !== parseInt(transferFilterMonth)) return false;
        if (transferFilterDay && day !== parseInt(transferFilterDay)) return false;
        
        return true;
      });
    }
    
    return filtered;
  };

  const filteredTransfers = getFilteredTransfers();
  const totalTransfersPages = Math.ceil(filteredTransfers.length / TRANSFERS_PER_PAGE);
  const paginatedTransfers = filteredTransfers.slice(
    (transfersPage - 1) * TRANSFERS_PER_PAGE,
    transfersPage * TRANSFERS_PER_PAGE
  );

  // Reset page when filters change
  useEffect(() => {
    setTransfersPage(1);
  }, [transferFilterArea, transferFilterBranch, transferFilterSupply, transferFilterDay, transferFilterMonth, transferFilterYear, transfers]);

  // Get unique areas and branches from transfers for filter options
  const transferAreas = [...new Set(transfers.flatMap(t => [t.from_area_name, t.to_area_name]).filter(Boolean))];
  const transferBranches = [...new Set(transfers.flatMap(t => [t.from_branch_id, t.to_branch_id]).filter(Boolean))];
  
  // Get available years and months from transfers for filter options
  const transferYears = [...new Set(transfers.map(t => new Date(t.created_at).getFullYear()))].sort((a, b) => b - a);
  const transferMonths = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ];
  const daysInMonth = transferFilterMonth && transferFilterYear 
    ? new Date(parseInt(transferFilterYear), parseInt(transferFilterMonth), 0).getDate()
    : 31;

  useEffect(() => {
    fetchData();
    fetchNotifications();
  }, [branchId]);

  useEffect(() => {
    if (selectedArea) {
      fetchAreaStock(selectedArea.area_id);
      // Clear filters when changing area
      setCategoryFilter('');
      setSearchQuery('');
    }
  }, [selectedArea]);

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReport(reportType);
    }
  }, [activeTab, reportType, reportPeriodType, reportSelectedMonth, reportCustomStart, reportCustomEnd]);

  const fetchData = async () => {
    try {
      // Use optimized single endpoint for initial data
      const branchParams = branchId 
        ? `&branch_id=${branchId}&is_matriz=${isMatriz}` 
        : '';
      
      const res = await api.get(`/inventory/init-data?t=${Date.now()}${branchParams}`);
      const data = res.data;
      
      setAreas(data.areas);
      setStockSummary(data.stock_summary);
      setSupplies(data.supplies);
      setProcessedSupplies(data.processed_supplies);
      setTransfers(data.transfers);
      setAllBranches(data.branches);
      
      // Auto-select warehouse
      const warehouse = data.areas.find(a => a.area_type === 'warehouse');
      if (warehouse) {
        setSelectedArea(warehouse);
      }
    } catch (error) {
      toast.error('Error al cargar datos de inventario');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      // Build branch filter params
      const branchParams = branchId 
        ? `?branch_id=${branchId}&is_matriz=${isMatriz}` 
        : '';
      const res = await api.get(`/inventory/reports/low-stock${branchParams}`);
      const notifs = [];
      const now = new Date();
      
      // Create notifications from low stock alerts
      for (const area of res.data) {
        for (const item of area.low_stock_items || []) {
          const isOutOfStock = item.current === 0;
          notifs.push({
            id: `stock_${item.supply_id}_${area.area_id}`,
            type: isOutOfStock ? 'danger' : 'warning',
            title: isOutOfStock 
              ? `¡Agotado! ${item.supply_name}` 
              : `Stock bajo: ${item.supply_name}`,
            message: isOutOfStock
              ? `${item.supply_name} se ha agotado en ${area.area_name}. Se necesitan ${item.minimum} ${item.unit}.`
              : `Quedan ${item.current} ${item.unit} en ${area.area_name}. Mínimo requerido: ${item.minimum} ${item.unit}.`,
            area: area.area_name,
            supply: item.supply_name,
            current: item.current,
            minimum: item.minimum,
            unit: item.unit,
            needed: item.needed,
            date: now.toISOString(),
            read: false
          });
        }
      }
      
      setNotifications(notifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = (notifId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notifId ? { ...n, read: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const dismissNotification = (notifId) => {
    setNotifications(prev => prev.filter(n => n.id !== notifId));
  };

  const fetchAreaStock = async (areaId) => {
    try {
      // Add timestamp and branch filter to prevent caching
      const branchParams = branchId 
        ? `&branch_id=${branchId}&is_matriz=${isMatriz}` 
        : '';
      const res = await api.get(`/inventory/areas/${areaId}/stock?t=${Date.now()}${branchParams}`);
      setAreaStock(res.data);
    } catch (error) {
      toast.error('Error al cargar stock del área');
    }
  };

  // Quick Adjustment Functions
  const openQuickAdjustment = (item) => {
    setQuickAdjustItem(item);
    setQuickAdjustForm({
      direction: 'restar',
      adjustment_type: 'merma',
      quantity: '',
      reason: ''
    });
    setQuickAdjustModalOpen(true);
  };

  const setQuickAdjustDirection = (direction) => {
    setQuickAdjustForm({
      ...quickAdjustForm,
      direction,
      adjustment_type: direction === 'sumar' ? 'ajuste' : 'merma'
    });
  };

  const handleQuickAdjustSubmit = async (e) => {
    e.preventDefault();
    if (!quickAdjustItem) return;

    const qty = parseFloat(quickAdjustForm.quantity);
    if (isNaN(qty) || qty <= 0) {
      toast.error('Ingrese una cantidad válida');
      return;
    }

    if (!quickAdjustForm.reason.trim()) {
      toast.error('Ingrese un motivo');
      return;
    }

    try {
      await api.post('/stock-adjustments', {
        supply_id: quickAdjustItem.supply_id,
        item_type: 'supply',
        item_name: quickAdjustItem.supply_name,
        adjustment_type: quickAdjustForm.adjustment_type,
        quantity: quickAdjustForm.direction === 'sumar' ? Math.abs(qty) : -Math.abs(qty),
        unit: quickAdjustItem.unit,
        reason: quickAdjustForm.reason,
        area_id: selectedArea?.area_id,
        cost_per_unit: quickAdjustItem.cost_per_unit || 0
      });
      
      toast.success('Ajuste registrado correctamente');
      setQuickAdjustModalOpen(false);
      setQuickAdjustItem(null);
      
      // Refresh area stock
      if (selectedArea) {
        fetchAreaStock(selectedArea.area_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar ajuste');
    }
  };

  const fetchReport = async (type) => {
    setLoadingReport(true);
    setExpandedMovementProducts({}); // Reset expanded products
    try {
      const { days, start, end } = getReportDateRange();
      // Para custom, usar fechas exactas
      let params = `days=${Math.max(days, 1)}`;
      if (reportPeriodType === 'custom' && reportCustomStart && reportCustomEnd) {
        params = `start_date=${reportCustomStart}&end_date=${reportCustomEnd}`;
      }
      
      if (type === 'consumption') {
        const res = await api.get(`/inventory/reports/consumption-by-product?${params}`);
        setConsumptionReport(res.data);
      } else if (type === 'movements') {
        const res = await api.get(`/inventory/reports/movements?${params}`);
        setMovementsReport(res.data);
      } else if (type === 'purchases') {
        const res = await api.get(`/inventory/reports/purchases?${params}`);
        setPurchasesReport(res.data);
      }
    } catch (error) {
      toast.error('Error al cargar reporte');
    } finally {
      setLoadingReport(false);
    }
  };

  // Fetch stock for transfer source area
  const fetchTransferSourceStock = async (areaId) => {
    if (!areaId) {
      setTransferSourceStock([]);
      return;
    }
    try {
      // Add timestamp to prevent caching
      const res = await api.get(`/inventory/areas/${areaId}/stock?t=${Date.now()}`);
      setTransferSourceStock(res.data.filter(item => item.quantity > 0));
    } catch (error) {
      console.error('Error fetching source stock:', error);
      setTransferSourceStock([]);
    }
  };

  // Handle transfer area origin change
  const handleTransferFromAreaChange = (areaId) => {
    setTransferForm(prev => ({ 
      ...prev, 
      from_area_id: areaId, 
      supply_id: '', 
      supply_search: '',
      quantity: ''
    }));
    setTransferItems([]); // Clear items when area changes
    setTransferItemType('supply');
    if (areaId) {
      fetchTransferSourceStock(areaId);
      fetchTransferSourceProcessed(areaId);
    } else {
      setTransferSourceStock([]);
      setTransferSourceProcessed([]);
    }
  };

  const fetchTransferSourceProcessed = async (areaId) => {
    try {
      // Get processed supplies - use available-to-produce endpoint
      // Add timestamp to prevent caching
      const res = await api.get(`/production/available-to-produce?t=${Date.now()}`);
      const allProcessed = res.data || [];
      // Include all processed supplies, we'll check stock later
      setTransferSourceProcessed(allProcessed.map(p => ({
        ...p,
        processed_id: p.processed_id,
        name: p.name,
        unit: p.unit,
        stock_quantity: p.current_stock || 0
      })));
    } catch (error) {
      console.log('Error fetching processed supplies:', error);
      setTransferSourceProcessed([]);
    }
  };

  const addTransferItem = () => {
    if (!transferForm.supply_id || !transferForm.quantity) {
      toast.error('Selecciona un insumo y cantidad');
      return;
    }
    
    const selectedItem = transferItemType === 'supply' 
      ? transferSourceStock.find(s => s.supply_id === transferForm.supply_id)
      : transferSourceProcessed.find(p => p.processed_id === transferForm.supply_id);
    
    if (!selectedItem) return;
    
    const qty = parseFloat(transferForm.quantity);
    const available = parseFloat(selectedItem.quantity || selectedItem.stock_quantity || 0);
    
    if (qty > available) {
      toast.error(`Solo hay ${available.toFixed(2)} disponibles`);
      return;
    }
    
    // Check if already in cart
    const existingIdx = transferItems.findIndex(i => 
      i.item_id === transferForm.supply_id && i.type === transferItemType
    );
    
    if (existingIdx >= 0) {
      // Update quantity
      const newItems = [...transferItems];
      newItems[existingIdx].quantity += qty;
      if (newItems[existingIdx].quantity > available) {
        toast.error(`No puedes traspasar más de ${available.toFixed(2)}`);
        return;
      }
      setTransferItems(newItems);
    } else {
      // Add new item
      setTransferItems(prev => [...prev, {
        item_id: transferForm.supply_id,
        type: transferItemType,
        name: selectedItem.supply_name || selectedItem.name,
        quantity: qty,
        unit: selectedItem.unit,
        available: available
      }]);
    }
    
    // Reset form but keep areas
    setTransferForm(prev => ({ 
      ...prev, 
      supply_id: '', 
      supply_search: '',
      quantity: '' 
    }));
  };

  const removeTransferItem = (idx) => {
    setTransferItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Download Vale de Solicitud PDF (without creating the transfer)
  const handleDownloadValeSolicitud = async () => {
    if (!transferForm.from_area_id || !transferForm.to_area_id) {
      toast.error('Selecciona las áreas de origen y destino');
      return;
    }
    
    try {
      // If no items, send empty array for blank vale
      const items = transferItems.length > 0 
        ? transferItems.map(item => ({
            supply_id: item.item_id,
            quantity: item.quantity,
            notes: ''
          }))
        : [];
      
      // Format date for display (DD/MM/YYYY)
      const fechaFormatted = valeFecha 
        ? new Date(valeFecha + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : null;
      
      const response = await api.post('/inventory/vale-solicitud/pdf', {
        from_area_id: transferForm.from_area_id,
        to_area_id: transferForm.to_area_id,
        items: items,
        solicitante: user?.name || '',
        business_name: 'MR FROGS',
        fecha: fechaFormatted
      }, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      // Check if response is valid
      if (response.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `vale_solicitud_${valeFecha || new Date().toISOString().slice(0,10)}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Vale de solicitud descargado');
      } else {
        toast.error('No se recibió el archivo PDF');
      }
    } catch (error) {
      console.error('Error descargando vale:', error);
      toast.error(error.response?.data?.detail || 'Error al generar el vale');
    }
  };

  // Download Vale de Solicitud COMPLETO con checklist de todos los insumos
  const handleDownloadValeCompleto = async () => {
    if (!transferForm.from_area_id || !transferForm.to_area_id) {
      toast.error('Selecciona las áreas de origen y destino');
      return;
    }
    
    try {
      const items = transferItems.length > 0 
        ? transferItems.map(item => ({
            supply_id: item.item_id,
            quantity: item.quantity,
            notes: ''
          }))
        : [];
      
      const fechaFormatted = valeFecha 
        ? new Date(valeFecha + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : null;
      
      const response = await api.post('/inventory/vale-solicitud-completo/pdf', {
        from_area_id: transferForm.from_area_id,
        to_area_id: transferForm.to_area_id,
        items: items,
        solicitante: user?.name || '',
        business_name: 'MR FROGS',
        fecha: fechaFormatted
      }, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });
      
      if (response.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `vale_checklist_${valeFecha || new Date().toISOString().slice(0,10)}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Vale con checklist completo descargado');
      } else {
        toast.error('No se recibió el archivo PDF');
      }
    } catch (error) {
      console.error('Error descargando vale completo:', error);
      toast.error(error.response?.data?.detail || 'Error al generar el vale');
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (transferItems.length === 0) {
      toast.error('Agrega al menos un insumo al traspaso');
      return;
    }
    
    // Doble protección contra múltiples clics
    if (savingTransfer || isSubmittingTransfer.current) return;
    isSubmittingTransfer.current = true;
    setSavingTransfer(true);
    
    try {
      // Use bulk transfer endpoint for multiple items
      const bulkItems = transferItems.map(item => ({
        supply_id: item.item_id,  // Backend uses supply_id for both supplies and processed
        quantity: item.quantity
      }));
      
      // Determine destination branch - only send if explicitly selecting another branch
      const destinationBranchId = transferForm.to_branch_id || null;
      
      await api.post('/inventory/transfers/bulk', {
        from_area_id: transferForm.from_area_id,
        to_area_id: transferForm.to_area_id || undefined,
        items: bulkItems,
        reason: transferForm.reason || 'reabastecimiento',
        notes: transferForm.notes || '',
        from_branch_id: branchId,  // Sucursal origen (la actual)
        to_branch_id: destinationBranchId || undefined  // Solo si es traspaso a otra sucursal
      });
      
      const destBranch = destinationBranchId ? allBranches.find(b => b.branch_id === destinationBranchId) : null;
      const destName = destBranch ? destBranch.name : 'el área destino';
      toast.success(`${transferItems.length} traspaso(s) realizado(s) a ${destName}`);
      
      // Store the from_area_id before clearing
      const previousFromArea = transferForm.from_area_id;
      
      setTransferModalOpen(false);
      setTransferForm({ from_area_id: '', to_area_id: '', supply_id: '', supply_search: '', quantity: '', reason: 'reabastecimiento', notes: '', to_branch_id: '' });
      setTransferSourceStock([]);
      setTransferSourceProcessed([]);
      setTransferItems([]);
      setValeFecha(new Date().toISOString().slice(0, 10));
      
      // Refresh data
      fetchData();
      if (selectedArea) {
        fetchAreaStock(selectedArea.area_id);
      }
      
      // Force refresh of source stock if modal is reopened with same area
      if (previousFromArea) {
        await fetchTransferSourceStock(previousFromArea);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al realizar traspaso');
    } finally {
      setSavingTransfer(false);
      isSubmittingTransfer.current = false;
    }
  };

  const handleSyncStock = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/inventory/warehouse/sync');
      toast.success(res.data.message);
      fetchData();
      if (selectedArea) {
        fetchAreaStock(selectedArea.area_id);
      }
    } catch (error) {
      toast.error('Error al sincronizar stock');
    } finally {
      setSyncing(false);
    }
  };

  // Adjustment functions
  const loadAdjustmentArea = async (areaId) => {
    // Usar caché si existe y tiene menos de 2 minutos
    const cached = adjustmentCacheRef.current[areaId];
    if (cached && (Date.now() - cached.timestamp) < 120000) {
      setAdjustmentArea(cached.data);
      setAdjustmentItems(cached.data.items.map(item => ({
        ...item,
        physical_count: item.system_quantity
      })));
      return;
    }
    
    setLoadingAdjustment(true);
    try {
      const res = await api.get(`/inventory/areas/${areaId}/for-adjustment`);
      // Guardar en caché
      adjustmentCacheRef.current[areaId] = {
        data: res.data,
        timestamp: Date.now()
      };
      setAdjustmentArea(res.data);
      setAdjustmentItems(res.data.items.map(item => ({
        ...item,
        physical_count: item.system_quantity
      })));
    } catch (error) {
      toast.error('Error al cargar datos del área');
    } finally {
      setLoadingAdjustment(false);
    }
  };

  const updatePhysicalCount = (supplyId, value) => {
    setAdjustmentItems(prev => 
      prev.map(item => 
        item.supply_id === supplyId 
          ? { ...item, physical_count: parseFloat(value) || 0 }
          : item
      )
    );
  };

  // Descargar PDF de inventario del área para ajuste físico
  const downloadAdjustmentPDF = () => {
    if (!adjustmentArea || adjustmentItems.length === 0) {
      toast.error('No hay datos para descargar');
      return;
    }
    
    const fecha = new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
    const hora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    
    const content = document.createElement('div');
    content.innerHTML = `
      <div style="font-family: Arial, sans-serif; padding: 20px; font-size: 11px;">
        <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #f97316; padding-bottom: 15px;">
          <h1 style="margin: 0; font-size: 18px; color: #333;">INVENTARIO FÍSICO</h1>
          <h2 style="margin: 5px 0; font-size: 14px; color: #f97316;">${adjustmentArea.area_name}</h2>
          <p style="margin: 5px 0; color: #666;">Fecha: ${fecha} | Hora: ${hora}</p>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
          <thead>
            <tr style="background: #f3f4f6;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 10px;">INSUMO</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 10px;">UNIDAD</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 10px;">CATEGORÍA</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 10px;">SISTEMA</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: center; font-size: 10px; width: 80px;">FÍSICO</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: right; font-size: 10px;">DIFERENCIA</th>
            </tr>
          </thead>
          <tbody>
            ${adjustmentItems.map((item, idx) => {
              const diff = (item.physical_count || 0) - item.system_quantity;
              const diffColor = diff < 0 ? '#dc2626' : diff > 0 ? '#16a34a' : '#666';
              const badges = [];
              if (item.is_elaborado) badges.push('<span style="background:#fef3c7;color:#d97706;padding:1px 4px;border-radius:3px;font-size:8px;margin-left:3px;">ELABORADO</span>');
              if (item.is_consumable) badges.push('<span style="background:#f3e8ff;color:#7c3aed;padding:1px 4px;border-radius:3px;font-size:8px;margin-left:3px;">CONSUMIBLE</span>');
              return `
                <tr style="background: ${idx % 2 === 0 ? '#fff' : '#fafafa'};">
                  <td style="border: 1px solid #ddd; padding: 6px; font-weight: 500;">${item.supply_name}${badges.join('')}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${item.unit}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: center;">${item.category || '-'}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right; font-family: monospace;">${item.system_quantity}</td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: center; border: 2px solid #f97316;"></td>
                  <td style="border: 1px solid #ddd; padding: 6px; text-align: right; font-family: monospace; color: ${diffColor};">${diff !== 0 ? (diff > 0 ? '+' : '') + diff.toFixed(2) : '-'}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
        <div style="margin-top: 30px; border-top: 1px solid #ddd; padding-top: 15px;">
          <div style="display: flex; justify-content: space-between;">
            <div style="width: 45%;">
              <p style="margin: 5px 0; font-size: 10px;"><strong>Razón del ajuste:</strong> _______________________</p>
              <p style="margin: 5px 0; font-size: 10px;"><strong>Notas:</strong> _________________________________</p>
            </div>
            <div style="width: 45%; text-align: right;">
              <p style="margin: 5px 0; font-size: 10px;"><strong>Realizado por:</strong> _______________________</p>
              <p style="margin: 5px 0; font-size: 10px;"><strong>Firma:</strong> _______________________</p>
            </div>
          </div>
        </div>
        <p style="text-align: center; margin-top: 20px; font-size: 9px; color: #999;">Total de insumos: ${adjustmentItems.length}</p>
      </div>
    `;
    
    html2pdf().set({
      margin: 10,
      filename: `inventario_${adjustmentArea.area_name.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    }).from(content).save();
    
    toast.success('PDF descargado');
  };

  const handleSubmitAdjustment = async () => {
    if (!adjustmentArea) return;
    
    // Filter only items that have changes
    const changedItems = adjustmentItems.filter(
      item => item.physical_count !== item.system_quantity
    );
    
    if (changedItems.length === 0) {
      toast.info('No hay cambios para ajustar');
      return;
    }
    
    try {
      const res = await api.post('/inventory/adjustments', {
        area_id: adjustmentArea.area_id,
        items: changedItems.map(item => ({
          supply_id: item.supply_id,
          physical_count: item.physical_count
        })),
        reason: adjustmentReason,
        notes: adjustmentNotes,
        fecha: adjustmentDate
      });
      
      toast.success(res.data.message);
      // Invalidar caché del área ajustada
      if (adjustmentArea?.area_id) {
        delete adjustmentCacheRef.current[adjustmentArea.area_id];
      }
      setAdjustmentArea(null);
      setAdjustmentItems([]);
      setAdjustmentReason('');
      setAdjustmentNotes('');
      setAdjustmentDate(new Date().toISOString().slice(0, 10));
      fetchData();
      fetchAdjustmentHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al realizar ajuste');
    }
  };

  const fetchAdjustmentHistory = async () => {
    try {
      const [historyRes, summaryRes] = await Promise.all([
        api.get('/inventory/adjustments?days=30'),
        api.get('/inventory/adjustments/summary/report?days=30')
      ]);
      setAdjustmentHistory(historyRes.data);
      setAdjustmentSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching adjustment history:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'adjustments') {
      fetchAdjustmentHistory();
    }
  }, [activeTab]);

  const allSupplies = [...supplies, ...processedSupplies.map(p => ({
    ...p,
    supply_id: p.processed_id,
    isProcessed: true
  }))];

  // Get unique categories from current area stock
  const stockCategories = [...new Set(areaStock.map(item => item.category).filter(Boolean))].sort();

  const printAreaStock = (cats = null) => {
    const selectedCats = cats && cats.length > 0 ? cats : (categoryFilter ? [categoryFilter] : []);
    const items = areaStock.filter(i =>
      (selectedCats.length === 0 || selectedCats.includes(i.category)) &&
      i.supply_name?.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => (a.category || '').localeCompare(b.category || '') || (a.supply_name || '').localeCompare(b.supply_name || ''));
    if (items.length === 0) return toast.error('No hay insumos para imprimir');
    const catLabel = selectedCats.length === 0 ? 'Todas' : selectedCats.join(', ');
    const fecha = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    const totalValor = items.reduce((s, i) => s + (i.value || 0), 0);
    let lastCat = null;
    const rowsHtml = items.map((i, idx) => {
      const header = (selectedCats.length !== 1 && i.category !== lastCat)
        ? `<tr style="background:#e5e5e5;"><td colspan="7" style="padding:6px 7px; font-weight:800; font-size:12px; letter-spacing:.5px;">${(i.category || 'SIN CATEGORÍA').toUpperCase()}</td></tr>`
        : '';
      lastCat = i.category;
      return header + `
              <tr style="border-bottom:1px solid #bbb; height:32px; ${i.quantity <= (i.min_stock || 0) ? 'background:#fff3f3;' : ''}">
                <td style="padding:6px 7px; color:#666;">${idx + 1}</td>
                <td style="padding:6px 7px; font-weight:700;">${i.supply_name || ''}</td>
                <td style="padding:6px 7px; color:#444;">${i.category || ''}</td>
                <td style="padding:6px 7px; text-align:right; font-weight:700;">${Number(i.quantity || 0).toFixed(2)}</td>
                <td style="padding:6px 7px;">${i.unit || ''}</td>
                <td style="padding:6px 7px; border-left:1px dashed #999; border-right:1px dashed #999;"></td>
                <td style="padding:6px 7px; text-align:right;">$${Number(i.value || 0).toFixed(2)}</td>
              </tr>`;
    }).join('');
    const html = `
      <div style="font-family: Arial, sans-serif; padding: 24px; color:#111;">
        <div style="border-bottom:3px solid #111; padding-bottom:8px; display:flex; justify-content:space-between; align-items:flex-end;">
          <div>
            <div style="font-size:20px; font-weight:800; letter-spacing:1px;">INVENTARIO · ${(selectedArea?.name || '').toUpperCase()}</div>
            <div style="font-size:13px; color:#333; margin-top:2px;"><b>Categorías:</b> ${catLabel}${searchQuery ? ` · Búsqueda: "${searchQuery}"` : ''}</div>
            <div style="font-size:12px; color:#555; text-transform:capitalize;">${fecha}</div>
          </div>
          <div style="font-size:12px; text-align:right;">
            <div><b>Responsable:</b> ${user?.name || ''}</div>
            <div style="margin-top:4px;">Contó: ______________________</div>
          </div>
        </div>
        <table style="width:100%; border-collapse:collapse; margin-top:14px; font-size:13px;">
          <thead><tr style="background:#111; color:#fff;">
            <th style="padding:7px; text-align:left; width:30px;">#</th>
            <th style="padding:7px; text-align:left;">Insumo</th>
            <th style="padding:7px; text-align:left; width:110px;">Categoría</th>
            <th style="padding:7px; text-align:right; width:90px;">Sistema</th>
            <th style="padding:7px; text-align:left; width:60px;">Unidad</th>
            <th style="padding:7px; text-align:right; width:90px;">Físico</th>
            <th style="padding:7px; text-align:right; width:90px;">Valor</th>
          </tr></thead>
          <tbody>
            ${rowsHtml}
            <tr><td colspan="6" style="padding:9px 7px; text-align:right; font-weight:700;">${items.length} insumo(s) · Valor total</td><td style="padding:9px 7px; text-align:right; font-weight:800;">$${totalValor.toFixed(2)}</td></tr>
          </tbody>
        </table>
        <div style="margin-top:8px; font-size:11px; color:#777;">Filas sombreadas = stock en o por debajo del mínimo. La columna "Físico" es para anotar el conteo a mano.</div>
      </div>`;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Inventario ${selectedArea?.name || ''}</title></head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
    setPrintCatsOpen(false);
  };

  const filteredStock = areaStock.filter(item => {
    const matchesSearch = item.supply_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getAreaIcon = (areaName) => {
    const Icon = AREA_ICONS[areaName] || Package;
    return Icon;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 md:ml-64 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
        </main>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  const tabs = [
    { id: 'stock', label: 'Stock por Área', icon: Warehouse },
    { id: 'adjustments', label: 'Ajuste Físico', icon: ClipboardList },
    { id: 'exit_format', label: 'Formato de Salidas por Ajuste', icon: FileText },
    { id: 'transfers', label: 'Traspasos', icon: ArrowRightLeft },
    { id: 'notifications', label: 'Notificaciones', icon: Bell, badge: unreadCount },
    { id: 'reports', label: 'Reportes', icon: BarChart3 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 md:ml-64 flex flex-col min-h-screen">
        {/* Header */}
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Inventario por Áreas</h1>
              <p className="text-sm text-gray-500">Control de stock, traspasos y reportes</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleSyncStock} 
                disabled={syncing}
                data-testid="sync-stock-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Sincronizar Stock'}
              </Button>
              <Button onClick={() => {
                // Reset form state when opening modal to ensure clean state
                setTransferForm({ from_area_id: '', to_area_id: '', supply_id: '', supply_search: '', quantity: '', reason: 'reabastecimiento', notes: '' });
                setTransferSourceStock([]);
                setTransferSourceProcessed([]);
                setTransferItems([]);
                setTransferModalOpen(true);
              }} data-testid="new-transfer-btn">
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Nuevo Traspaso
              </Button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 bg-gray-100 p-1 rounded-lg w-fit">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  type="button"
                  key={tab.id}
                  onClick={(e) => { e.preventDefault(); handleTabChange(tab.id); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors relative ${
                    activeTab === tab.id
                      ? 'bg-white text-orange-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.badge > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {/* TAB: Notificaciones */}
          {activeTab === 'notifications' && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-orange-500" />
                    Bandeja de Notificaciones
                  </CardTitle>
                  {notifications.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllAsRead}>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Marcar todo como leído
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {notifications.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Bell className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No hay notificaciones</p>
                    <p className="text-sm">Cuando haya alertas importantes aparecerán aquí</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {notifications.map(notif => (
                      <div 
                        key={notif.id}
                        className={`flex items-start gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                          !notif.read ? 'bg-orange-50/50' : ''
                        }`}
                        onClick={() => markAsRead(notif.id)}
                      >
                        {/* Icon indicator */}
                        <div className={`p-2 rounded-full ${
                          notif.type === 'danger' ? 'bg-red-100' : 'bg-yellow-100'
                        }`}>
                          {notif.type === 'danger' ? (
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-yellow-600" />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {!notif.read && (
                              <Circle className="w-2 h-2 fill-orange-500 text-orange-500" />
                            )}
                            <h4 className={`font-medium ${!notif.read ? 'text-gray-900' : 'text-gray-600'}`}>
                              {notif.title}
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {notif.message}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Warehouse className="w-3 h-3" />
                              {notif.area}
                            </span>
                            <span>
                              {new Date(notif.date).toLocaleDateString('es-MX', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                        
                        {/* Action */}
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-mono px-2 py-1 rounded ${
                            notif.type === 'danger' 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {notif.current === 0 ? 'AGOTADO' : `${notif.current} ${notif.unit}`}
                          </span>
                          <button 
                            onClick={(e) => { e.stopPropagation(); dismissNotification(notif.id); }}
                            className="text-gray-400 hover:text-gray-600 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* TAB: Stock por Área */}
          {activeTab === 'stock' && (
            <div className="space-y-4">
              {/* Search Bar for Areas - Always visible */}
              <div className="flex items-center gap-3 mb-2">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar área..."
                    value={areaSearchQuery}
                    onChange={(e) => setAreaSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="area-search-input"
                  />
                  {areaSearchQuery && (
                    <button
                      onClick={() => setAreaSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-3 h-3 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex gap-6">
                {/* Left: Areas Summary */}
                <div className={`transition-all duration-300 ${selectedArea ? 'w-1/3' : 'w-full'}`}>
                  <div className={`grid ${selectedArea ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'} gap-4`}>
                    {stockSummary
                      .filter(area => {
                        if (!areaSearchQuery.trim()) return true;
                        const search = areaSearchQuery.toLowerCase();
                        return area.area_name?.toLowerCase().includes(search);
                      })
                      .map(area => {
                      const Icon = getAreaIcon(area.area_name);
                      const isSelected = selectedArea?.area_id === area.area_id;
                    
                    return (
                      <div 
                        key={area.area_id}
                        className={`bg-white rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md ${
                          isSelected ? 'border-l-4 border-l-orange-500 bg-orange-50' : 'hover:border-orange-200'
                        }`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedArea(null);
                          } else {
                            setSelectedArea(areas.find(a => a.area_id === area.area_id));
                          }
                        }}
                        data-testid={`area-card-${area.area_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className={`p-2 rounded-lg ${
                            isSelected ? 'bg-orange-500' : area.area_type === 'warehouse' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              isSelected ? 'text-white' : area.area_type === 'warehouse' ? 'text-blue-600' : 'text-green-600'
                            }`} />
                          </div>
                          {area.low_stock_count > 0 && (
                            <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                              <AlertTriangle className="w-3 h-3" />
                              {area.low_stock_count}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-900">{area.area_name}</h3>
                        <div className="flex items-center justify-between mt-2 text-sm">
                          <span className="text-gray-500">{area.items_count} insumos</span>
                          <span className="font-bold text-green-600">${area.total_value.toFixed(0)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Area Detail Panel (FUDO style) */}
              {selectedArea && (
                <div className="w-2/3 bg-slate-800 rounded-xl text-white overflow-hidden flex flex-col max-h-[calc(100vh-220px)]">
                  {/* Panel Header */}
                  <div className="bg-slate-900 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
                        {(() => {
                          const Icon = getAreaIcon(selectedArea.name);
                          return <Icon className="w-6 h-6 text-white" />;
                        })()}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{selectedArea.name}</h3>
                        <p className="text-slate-400 text-sm">
                          {selectedArea.area_type === 'warehouse' ? 'Almacén' : 'Área de consumo'}
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setSelectedArea(null)}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Area Stats */}
                  <div className="grid grid-cols-3 gap-3 p-4 bg-slate-900/50">
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">{areaStock.length}</p>
                      <p className="text-xs text-slate-400">Insumos</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-400">
                        ${areaStock.reduce((sum, i) => sum + (i.value || 0), 0).toFixed(0)}
                      </p>
                      <p className="text-xs text-slate-400">Valor Total</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-400">
                        {areaStock.filter(i => i.quantity <= (i.min_stock || 0)).length}
                      </p>
                      <p className="text-xs text-slate-400">Stock Bajo</p>
                    </div>
                  </div>

                  {/* Search and Filter */}
                  <div className="p-4 bg-slate-900/30 flex gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Buscar insumo..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-slate-700/50 border-0 rounded-lg pl-9 pr-4 py-2 text-white placeholder-slate-400 focus:ring-2 focus:ring-orange-500"
                        data-testid="stock-search"
                      />
                    </div>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="bg-slate-700/50 border-0 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-orange-500"
                      data-testid="category-filter"
                    >
                      <option value="">Todas</option>
                      {stockCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => { setPrintCats(categoryFilter ? [categoryFilter] : []); setPrintCatsOpen(true); }}
                      className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-3 py-2 flex items-center gap-2 text-sm font-medium"
                      title="Imprimir por categorías"
                      data-testid="print-area-stock-btn"
                    >
                      <Printer className="w-4 h-4" />
                      Imprimir
                    </button>
                  </div>

                  <Modal open={printCatsOpen} onClose={() => setPrintCatsOpen(false)} title={`Imprimir inventario · ${selectedArea?.name || ''}`} size="md">
                    <div className="space-y-4" data-testid="print-cats-modal">
                      <p className="text-sm text-slate-600">Marca las categorías que quieres imprimir juntas. Sin marcar ninguna se imprime todo el área.</p>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setPrintCats([...stockCategories])} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid="print-cats-all">Marcar todas</button>
                        <button type="button" onClick={() => setPrintCats([])} className="text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid="print-cats-none">Quitar todas</button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                        {stockCategories.map(cat => {
                          const count = areaStock.filter(i => i.category === cat).length;
                          const checked = printCats.includes(cat);
                          return (
                            <label key={cat} className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer ${checked ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`print-cat-${cat}`}>
                              <input type="checkbox" checked={checked} onChange={() => setPrintCats(p => checked ? p.filter(c => c !== cat) : [...p, cat])} className="accent-orange-500" />
                              <span className="flex-1 text-sm font-medium text-slate-800">{cat}</span>
                              <span className="text-xs text-slate-500">{count}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="text-sm text-slate-600" data-testid="print-cats-summary">
                          {printCats.length === 0 ? `Todo el área (${areaStock.length} insumos)` : `${printCats.length} categoría(s) · ${areaStock.filter(i => printCats.includes(i.category)).length} insumos`}
                        </span>
                        <Button onClick={() => printAreaStock(printCats)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="print-cats-confirm">
                          <Printer className="w-4 h-4 mr-2" /> Imprimir
                        </Button>
                      </div>
                    </div>
                  </Modal>

                  {/* Stock List - Professional Table View */}
                  <div className="flex-1 overflow-y-auto">
                    {filteredStock.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No se encontraron insumos</p>
                      </div>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-slate-900/80 sticky top-0">
                          <tr className="text-xs text-slate-400 uppercase">
                            <th className="text-left px-4 py-3">Insumo</th>
                            <th className="text-right px-4 py-3">Stock Actual</th>
                            <th className="text-right px-4 py-3">Mínimo</th>
                            <th className="text-right px-4 py-3">Valor</th>
                            <th className="text-center px-4 py-3">Última Actualización</th>
                            <th className="text-center px-4 py-3">Acción</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                          {filteredStock.map((item, idx) => {
                            const isLowStock = item.quantity <= (item.min_stock || 0);
                            const lastUpdate = item.last_updated ? new Date(item.last_updated) : null;
                            const daysAgo = lastUpdate ? Math.floor((new Date() - lastUpdate) / (1000 * 60 * 60 * 24)) : null;
                            
                            return (
                              <tr 
                                key={idx} 
                                className={`hover:bg-slate-700/30 transition-colors ${isLowStock ? 'bg-red-500/10' : ''}`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-2 h-8 rounded-full ${isLowStock ? 'bg-red-500' : 'bg-green-500'}`} />
                                    <div>
                                      <p className="font-medium text-white">{item.supply_name}</p>
                                      <p className="text-xs text-slate-400">{item.category || 'Sin categoría'}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span className={`font-mono font-bold text-lg ${isLowStock ? 'text-red-400' : 'text-green-400'}`}>
                                    {item.quantity.toFixed(2)}
                                  </span>
                                  <span className="text-slate-400 text-sm ml-1">{item.unit}</span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span className="font-mono text-slate-400">
                                    {item.min_stock || 0} {item.unit}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span className="font-medium text-green-400">
                                    ${(item.value || 0).toFixed(2)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  {lastUpdate ? (
                                    <div className="text-xs">
                                      <p className="text-slate-300">
                                        {lastUpdate.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                                      </p>
                                      <p className="text-slate-500">
                                        {daysAgo === 0 ? 'Hoy' : daysAgo === 1 ? 'Ayer' : `Hace ${daysAgo} días`}
                                      </p>
                                    </div>
                                  ) : (
                                    <span className="text-slate-500 text-xs">-</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => openQuickAdjustment(item)}
                                    data-testid={`quick-adjust-open-${item.supply_id}`}
                                    className="p-2 bg-orange-500/20 hover:bg-orange-500/40 rounded-lg text-orange-400 hover:text-orange-300 transition-colors"
                                    title="Ajuste rápido (Sumar / Restar)"
                                  >
                                    <ShieldAlert className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

          {/* TAB: Ajuste Físico */}
          {activeTab === 'exit_format' && (
            <ExitAdjustmentFormat
              warehouses={areas.filter(a => a.area_type === 'warehouse')}
              onRegistered={() => { fetchData(); if (selectedArea) fetchAreaStock(selectedArea.area_id); }}
            />
          )}

          {activeTab === 'adjustments' && (
            <div className="space-y-6">
              {/* Area selector or adjustment form */}
              {!adjustmentArea ? (
                <>
                  {/* Summary Cards */}
                  {adjustmentSummary && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <ClipboardList className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Ajustes (30 días)</p>
                              <p className="text-2xl font-bold">{adjustmentSummary.total_adjustments}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-red-100 rounded-lg">
                              <TrendingDown className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Pérdidas</p>
                              <p className="text-2xl font-bold text-red-600">-${adjustmentSummary.total_loss}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <TrendingUp className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Ganancias</p>
                              <p className="text-2xl font-bold text-green-600">+${adjustmentSummary.total_gain}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${adjustmentSummary.net_difference < 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                              <DollarSign className={`w-5 h-5 ${adjustmentSummary.net_difference < 0 ? 'text-red-600' : 'text-green-600'}`} />
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Diferencia Neta</p>
                              <p className={`text-2xl font-bold ${adjustmentSummary.net_difference < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                ${adjustmentSummary.net_difference}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {/* Select area to adjust */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Seleccionar Área para Ajuste</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Search for areas */}
                      <div className="mb-4">
                        <div className="relative max-w-md">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Buscar área..."
                            value={areaSearchQuery}
                            onChange={(e) => setAreaSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="adjustment-area-search"
                          />
                          {areaSearchQuery && (
                            <button
                              onClick={() => setAreaSearchQuery('')}
                              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                            >
                              <X className="w-3 h-3 text-gray-400" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {areas
                          .filter(area => {
                            if (!areaSearchQuery.trim()) return true;
                            return area.name?.toLowerCase().includes(areaSearchQuery.toLowerCase());
                          })
                          .map(area => {
                          const Icon = getAreaIcon(area.name);
                          return (
                            <button
                              key={area.area_id}
                              onClick={() => loadAdjustmentArea(area.area_id)}
                              className="p-4 border rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                              data-testid={`adjust-area-${area.area_id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-100 rounded-lg">
                                  <Icon className="w-5 h-5 text-gray-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{area.name}</p>
                                  <p className="text-sm text-gray-500">Realizar ajuste físico</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Problematic supplies */}
                  {adjustmentSummary?.problematic_supplies?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-red-600">
                          <AlertTriangle className="w-5 h-5" />
                          Insumos Problemáticos (Mayor diferencia)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left p-3 text-sm font-medium text-gray-600">Insumo</th>
                                <th className="text-right p-3 text-sm font-medium text-gray-600">Ajustes</th>
                                <th className="text-right p-3 text-sm font-medium text-gray-600">Dif. Cantidad</th>
                                <th className="text-right p-3 text-sm font-medium text-gray-600">Dif. Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {adjustmentSummary.problematic_supplies.map((supply, idx) => (
                                <tr key={idx} className="border-t">
                                  <td className="p-3 font-medium">{supply.name}</td>
                                  <td className="p-3 text-right">{supply.adjustment_count}</td>
                                  <td className={`p-3 text-right font-mono ${supply.total_difference_qty < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {supply.total_difference_qty > 0 ? '+' : ''}{supply.total_difference_qty.toFixed(2)} {supply.unit}
                                  </td>
                                  <td className={`p-3 text-right font-mono font-bold ${supply.total_difference_value < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {supply.total_difference_value > 0 ? '+' : ''}${supply.total_difference_value.toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Adjustment History */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <History className="w-5 h-5 text-orange-500" />
                        Historial de Ajustes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {adjustmentHistory.length === 0 ? (
                        <p className="text-center py-8 text-gray-500">No hay ajustes registrados</p>
                      ) : (
                        <div className="space-y-3">
                          {adjustmentHistory.map(adj => (
                            <div key={adj.adjustment_id} className="border rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="font-medium">{adj.area_name}</p>
                                  <p className="text-sm text-gray-500">
                                    {new Date(adj.created_at).toLocaleDateString('es-MX', {
                                      day: '2-digit',
                                      month: 'long',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className={`font-mono font-bold ${adj.total_difference_value < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {adj.total_difference_value > 0 ? '+' : ''}${adj.total_difference_value.toFixed(2)}
                                  </p>
                                  {adj.reason && (
                                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                      {adj.reason}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {adj.items?.slice(0, 5).map((item, i) => (
                                  <span key={i} className={`text-xs px-2 py-1 rounded ${
                                    item.difference < 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                                  }`}>
                                    {item.supply_name}: {item.difference > 0 ? '+' : ''}{item.difference} {item.unit}
                                  </span>
                                ))}
                                {adj.items?.length > 5 && (
                                  <span className="text-xs text-gray-500">+{adj.items.length - 5} más</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                /* Adjustment Form */
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-orange-500" />
                        Ajuste de Inventario: {adjustmentArea.area_name}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={downloadAdjustmentPDF} className="text-orange-600 border-orange-300 hover:bg-orange-50">
                          <FileDown className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        <Button variant="outline" onClick={() => setAdjustmentArea(null)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingAdjustment ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* Instructions */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <p className="text-sm text-blue-800">
                            <strong>Instrucciones:</strong> Ingresa el conteo físico real de cada insumo. 
                            El sistema calculará automáticamente la diferencia.
                          </p>
                        </div>

                        {/* Reason and notes */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label>Razón del Ajuste</Label>
                            <select
                              value={adjustmentReason}
                              onChange={(e) => setAdjustmentReason(e.target.value)}
                              className="w-full mt-1 border rounded-md p-2"
                              data-testid="adjustment-reason-select"
                            >
                              <option value="">Seleccionar razón</option>
                              <optgroup label="📥 ENTRADAS (Stock aumenta)">
                                <option value="conteo_fisico">Conteo físico - Stock real mayor</option>
                                <option value="devolucion_produccion">Devolución de producción</option>
                                <option value="devolucion_area">Devolución de área</option>
                                <option value="correccion_error">Corrección de error</option>
                                <option value="entrada_sin_factura">Entrada sin factura/Donación</option>
                                <option value="sobrante_inventario">Sobrante de inventario</option>
                              </optgroup>
                              <optgroup label="📤 SALIDAS (Stock disminuye)">
                                <option value="merma_deterioro">Merma por deterioro/daño</option>
                                <option value="producto_vencido">Producto vencido/caducado</option>
                                <option value="perdida_extravio">Pérdida/Extravío</option>
                                <option value="robo_confirmado">Robo confirmado</option>
                                <option value="uso_interno">Uso interno (empleados/degustación)</option>
                                <option value="consumo_produccion">Consumo para producción</option>
                                <option value="cortesia_regalo">Cortesía/Regalo a cliente</option>
                                <option value="error_conteo">Error de conteo anterior</option>
                                <option value="desperdicio_preparacion">Desperdicio en preparación</option>
                                <option value="muestra_proveedor">Muestra para proveedor</option>
                              </optgroup>
                              <optgroup label="🔄 OTROS">
                                <option value="conteo_rutinario">Conteo rutinario</option>
                                <option value="otro_entrada">Otro ajuste de entrada</option>
                                <option value="otro_salida">Otro ajuste de salida</option>
                              </optgroup>
                            </select>
                          </div>
                          <div>
                            <Label>Notas</Label>
                            <Input
                              value={adjustmentNotes}
                              onChange={(e) => setAdjustmentNotes(e.target.value)}
                              placeholder="Notas adicionales..."
                              className="mt-1"
                            />
                          </div>
                        </div>

                        {/* Items table */}
                        <div className="mb-4">
                          <div className="relative max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              type="text"
                              placeholder="Buscar insumo..."
                              value={adjustmentSearch}
                              onChange={(e) => setAdjustmentSearch(e.target.value)}
                              className="pl-10"
                              data-testid="adjustment-supply-search"
                            />
                            {adjustmentSearch && (
                              <button
                                onClick={() => setAdjustmentSearch('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                              >
                                <X className="w-3 h-3 text-gray-400" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left p-3 text-sm font-medium text-gray-600">Insumo</th>
                                <th className="text-left p-3 text-sm font-medium text-gray-600">Categoría</th>
                                <th className="text-right p-3 text-sm font-medium text-gray-600">Sistema</th>
                                <th className="text-center p-3 text-sm font-medium text-gray-600">Conteo Físico</th>
                                <th className="text-right p-3 text-sm font-medium text-gray-600">Diferencia</th>
                                <th className="text-right p-3 text-sm font-medium text-gray-600">Valor Dif.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {adjustmentItems
                                .filter(item => {
                                  if (!adjustmentSearch.trim()) return true;
                                  const search = adjustmentSearch.toLowerCase();
                                  return (
                                    item.supply_name?.toLowerCase().includes(search) ||
                                    item.category?.toLowerCase().includes(search)
                                  );
                                })
                                .map((item, idx) => {
                                const diff = (item.physical_count || 0) - item.system_quantity;
                                const diffValue = diff * item.cost_per_unit;
                                return (
                                  <tr key={idx} className={`border-t ${diff !== 0 ? (diff < 0 ? 'bg-red-50' : 'bg-green-50') : ''}`}>
                                    <td className="p-3">
                                      <span className="font-medium">{item.supply_name}</span>
                                      <span className="text-sm text-gray-500 ml-2">({item.unit})</span>
                                      {item.is_elaborado && (
                                        <span className="ml-2 bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                          Elaborado
                                        </span>
                                      )}
                                      {item.is_consumable && (
                                        <span className="ml-2 bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                          Consumible
                                        </span>
                                      )}
                                    </td>
                                    <td className="p-3">
                                      {item.category ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 whitespace-nowrap">
                                          {item.category}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400 text-sm">-</span>
                                      )}
                                    </td>
                                    <td className="p-3 text-right font-mono">
                                      {item.system_quantity} {item.unit}
                                    </td>
                                    <td className="p-3">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={item.physical_count}
                                        onChange={(e) => updatePhysicalCount(item.supply_id, e.target.value)}
                                        className="w-24 mx-auto text-center"
                                        data-testid={`physical-count-${item.supply_id}`}
                                      />
                                    </td>
                                    <td className={`p-3 text-right font-mono font-bold ${
                                      diff < 0 ? 'text-red-600' : diff > 0 ? 'text-green-600' : 'text-gray-400'
                                    }`}>
                                      {diff !== 0 ? (diff > 0 ? '+' : '') + diff.toFixed(2) : '-'}
                                    </td>
                                    <td className={`p-3 text-right font-mono ${
                                      diffValue < 0 ? 'text-red-600' : diffValue > 0 ? 'text-green-600' : 'text-gray-400'
                                    }`}>
                                      {diffValue !== 0 ? (diffValue > 0 ? '+$' : '-$') + Math.abs(diffValue).toFixed(2) : '-'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-gray-100 font-bold">
                              <tr>
                                <td colSpan="5" className="p-3 text-right">Total Diferencia:</td>
                                <td className={`p-3 text-right font-mono ${
                                  adjustmentItems.reduce((sum, item) => {
                                    const diff = (item.physical_count || 0) - item.system_quantity;
                                    return sum + (diff * item.cost_per_unit);
                                  }, 0) < 0 ? 'text-red-600' : 'text-green-600'
                                }`}>
                                  ${adjustmentItems.reduce((sum, item) => {
                                    const diff = (item.physical_count || 0) - item.system_quantity;
                                    return sum + (diff * item.cost_per_unit);
                                  }, 0).toFixed(2)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {/* Submit button */}
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setAdjustmentArea(null)}>
                            Cancelar
                          </Button>
                          <Button 
                            onClick={handleSubmitAdjustment}
                            className="bg-orange-500 hover:bg-orange-600"
                            data-testid="submit-adjustment"
                          >
                            Guardar Ajuste
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* TAB: Traspasos */}
          {activeTab === 'transfers' && (
            <Card>
              <CardHeader className="border-b bg-gradient-to-r from-slate-50 to-white pb-2">
                <CardTitle className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <History className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-gray-900">Historial de Traspasos</h2>
                        <p className="text-sm text-gray-500 font-normal">Registro detallado de movimientos entre áreas</p>
                      </div>
                    </div>
                  </div>
                  {/* Filtros Colapsables - Limpio */}
                  <div className="flex items-center justify-between gap-3">
                    {/* Botón de Filtros + Limpiar */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={showTransferFilters ? "default" : "outline"}
                        onClick={() => setShowTransferFilters(!showTransferFilters)}
                        className={showTransferFilters ? "bg-orange-500 hover:bg-orange-600" : ""}
                      >
                        <Filter className="w-4 h-4 mr-1" />
                        Filtros
                        <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showTransferFilters ? 'rotate-90' : ''}`} />
                      </Button>
                      
                      {(transferFilterArea || transferFilterBranch || transferFilterSupply || transferFilterDay || transferFilterMonth || transferFilterYear) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setTransferFilterArea('');
                            setTransferFilterBranch('');
                            setTransferFilterSupply('');
                            setTransferFilterDay('');
                            setTransferFilterMonth('');
                            setTransferFilterYear('');
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Limpiar
                        </Button>
                      )}
                    </div>
                    
                    {/* Contador de Resultados */}
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-slate-600 bg-white px-4 py-1.5 rounded-full border shadow-sm">
                        Mostrando: <span className="font-bold text-orange-600">{filteredTransfers.length}</span>
                        <span className="text-slate-400"> de {transfers.length}</span>
                      </div>
                      
                      {/* Export buttons */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if(transfers.length===0){toast.error('No hay datos');return;}
                          const c={};transfers.forEach(t=>{const n=t.supply_name||t.supply_id;c[n]=(c[n]||0)+t.quantity;});
                          const top10=Object.entries(c).sort((a,b)=>b[1]-a[1]).slice(0,10);
                          const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Traspasos</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11px;padding:15px}.header{text-align:center;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:15px}.stats{display:flex;justify-content:space-around;margin-bottom:15px;padding:10px;background:#f5f5f5;border-radius:5px}.stat{text-align:center}.stat-value{font-size:14px;font-weight:bold}table{width:100%;border-collapse:collapse;font-size:10px}th{background:#f3f4f6;padding:6px;border:1px solid #ddd}td{padding:5px;border:1px solid #ddd}.top10{margin:15px 0;padding:10px;background:#fff8e1;border-radius:5px}.branch{font-size:9px;color:#666}</style></head><body><div class="header"><h1>${branchName||'RESTAURANTE'}</h1><p>HISTORIAL DE TRASPASOS</p><p>${transferDateFrom||''}${transferDateFrom&&transferDateTo?' - ':''}${transferDateTo||new Date().toLocaleDateString('es-MX')}</p></div><div class="stats"><div class="stat"><div class="stat-value">${transfers.length}</div><div>Traspasos</div></div><div class="stat"><div class="stat-value">$${transfers.reduce((s,t)=>s+(t.total_cost||0),0).toFixed(2)}</div><div>Valor Total</div></div></div><div class="top10"><strong>Top 10 Insumos:</strong> ${top10.map(([n,q],i)=>`${i+1}.${n}:${q.toFixed(1)}`).join(' | ')}</div><table><thead><tr><th>Fecha</th><th>Insumo</th><th>Cant</th><th>Origen</th><th>Destino</th><th>Costo</th></tr></thead><tbody>${transfers.map(t=>{const loc=getTransferLocationInfo(t);return `<tr><td>${new Date(t.created_at).toLocaleDateString('es-MX')}</td><td>${t.supply_name||t.supply_id}</td><td>${t.quantity}</td><td>${t.from_area_name||'-'}${loc.fromBranchName?`<br><span class="branch">${loc.fromBranchName}</span>`:''}</td><td>${t.to_area_name||'-'}${loc.toBranchName?`<br><span class="branch">${loc.toBranchName}</span>`:''}</td><td>$${(t.total_cost||0).toFixed(2)}</td></tr>`;}).join('')}</tbody></table></body></html>`;
                          const container=document.createElement('div');container.innerHTML=html;document.body.appendChild(container);
                          html2pdf().set({margin:10,filename:`traspasos_${new Date().toISOString().slice(0,10)}.pdf`,html2canvas:{scale:2},jsPDF:{format:'a4'}}).from(container).save().then(()=>{document.body.removeChild(container);toast.success('PDF descargado');});
                        }}
                      >
                        <FileDown className="w-4 h-4 mr-1" />PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if(transfers.length===0){toast.error('No hay datos');return;}
                          const h=['Fecha','Hora','Insumo','Cantidad','Unidad','Origen','Sucursal Origen','Destino','Sucursal Destino','Costo'];
                          const r=transfers.map(t=>{const loc=getTransferLocationInfo(t);return [new Date(t.created_at).toLocaleDateString('es-MX'),new Date(t.created_at).toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'}),t.supply_name||t.supply_id,t.quantity,t.unit||'',t.from_area_name||'',loc.fromBranchName||'',t.to_area_name||'',loc.toBranchName||'',(t.total_cost||0).toFixed(2)];});
                          const csv=[h,...r].map(row=>row.map(c=>`"${c}"`).join(',')).join('\n');
                          const blob=new Blob(['\ufeff'+csv],{type:'text/csv;charset=utf-8;'});
                          const link=document.createElement('a');link.href=URL.createObjectURL(blob);link.download=`traspasos_${new Date().toISOString().slice(0,10)}.csv`;link.click();
                          toast.success('Excel descargado');
                        }}
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-1" />Excel
                      </Button>
                    </div>
                  </div>
                  
                  {/* Panel de Filtros Expandible */}
                  {showTransferFilters && (
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-4 animate-in slide-in-from-top-2">
                      {/* Fila 1: Filtros Rápidos de Período */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-slate-600 w-20">Período:</span>
                        {[{l:'Hoy',d:1},{l:'7 días',d:7},{l:'15 días',d:15},{l:'30 días',d:30},{l:'90 días',d:90},{l:'Año',d:365}].map(({l,d})=>(
                          <Button key={d} size="sm" variant="outline" className="text-xs h-8" onClick={async()=>{
                            try{const p=branchId?`&branch_id=${branchId}&is_matriz=${isMatriz}`:'';const r=await api.get(`/inventory/transfers?days=${d}${p}`);setTransfers(r.data);toast.success(`${r.data.length} en ${l}`);}catch(e){toast.error('Error');}
                          }}>{l}</Button>
                        ))}
                        <span className="text-slate-300 mx-2">|</span>
                        <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5 shadow-sm">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <input type="date" value={transferDateFrom} onChange={(e)=>setTransferDateFrom(e.target.value)} className="border-0 text-sm focus:ring-0 p-0 w-32 bg-transparent"/>
                          <span className="text-slate-300">→</span>
                          <input type="date" value={transferDateTo} onChange={(e)=>setTransferDateTo(e.target.value)} className="border-0 text-sm focus:ring-0 p-0 w-32 bg-transparent"/>
                        </div>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              const branchParams = branchId ? `&branch_id=${branchId}&is_matriz=${isMatriz}` : '';
                              let url = `/inventory/transfers?days=365${branchParams}`;
                              if (transferDateFrom) url += `&start_date=${transferDateFrom}`;
                              if (transferDateTo) url += `&end_date=${transferDateTo}`;
                              const res = await api.get(url);
                              setTransfers(res.data);
                              toast.success(`${res.data.length} traspasos encontrados`);
                            } catch (error) {
                              toast.error('Error al filtrar');
                            }
                          }}
                          className="bg-orange-500 hover:bg-orange-600 h-8"
                        >
                          Buscar
                        </Button>
                      </div>
                      
                      {/* Fila 2: Filtros de Fecha Específica */}
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-medium text-slate-600 w-20">Fecha:</span>
                        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
                          <select
                            value={transferFilterDay}
                            onChange={(e) => setTransferFilterDay(e.target.value)}
                            className="text-sm border-0 bg-transparent focus:ring-0 py-0.5 pr-8 font-medium"
                            data-testid="filter-day"
                          >
                            <option value="">Día</option>
                            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                              <option key={day} value={day}>{day}</option>
                            ))}
                          </select>
                          <span className="text-slate-300">/</span>
                          <select
                            value={transferFilterMonth}
                            onChange={(e) => {
                              setTransferFilterMonth(e.target.value);
                              setTransferFilterDay('');
                            }}
                            className="text-sm border-0 bg-transparent focus:ring-0 py-0.5 pr-8 font-medium"
                            data-testid="filter-month"
                          >
                            <option value="">Mes</option>
                            {transferMonths.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          <span className="text-slate-300">/</span>
                          <select
                            value={transferFilterYear}
                            onChange={(e) => {
                              setTransferFilterYear(e.target.value);
                              if (!e.target.value) {
                                setTransferFilterMonth('');
                                setTransferFilterDay('');
                              }
                            }}
                            className="text-sm border-0 bg-transparent focus:ring-0 py-0.5 pr-8 font-medium"
                            data-testid="filter-year"
                          >
                            <option value="">Año</option>
                            {transferYears.map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Fila 3: Filtros de Área y Búsqueda */}
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-medium text-slate-600 w-20">Filtrar:</span>
                        <select
                          value={transferFilterArea}
                          onChange={(e) => setTransferFilterArea(e.target.value)}
                          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-w-[180px]"
                          data-testid="filter-area"
                        >
                          <option value="">Todas las áreas</option>
                          {transferAreas.map(area => (
                            <option key={area} value={area}>{area}</option>
                          ))}
                        </select>
                        
                        {allBranches.length > 1 && (
                          <select
                            value={transferFilterBranch}
                            onChange={(e) => setTransferFilterBranch(e.target.value)}
                            className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 min-w-[180px]"
                            data-testid="filter-branch"
                          >
                            <option value="">Todas las sucursales</option>
                            {allBranches.map(branch => (
                              <option key={branch.branch_id} value={branch.branch_id}>{branch.name}</option>
                            ))}
                          </select>
                        )}
                        
                        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                          <div className="relative flex-1">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                              type="text"
                              value={transferFilterSupply}
                              onChange={(e) => setTransferFilterSupply(e.target.value)}
                              placeholder="Buscar insumo..."
                              className="text-sm border border-slate-200 rounded-lg pl-10 pr-3 py-2 bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-full"
                              data-testid="filter-supply"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Stats Summary - Clickeable */}
                  {transfers.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2 bg-orange-50 rounded-lg border border-orange-200 text-center">
                      <div className="cursor-pointer hover:bg-orange-100 rounded p-1 transition-colors" onClick={()=>setShowTimelinePanel(true)}>
                        <p className="text-xl font-bold text-orange-600">{transfers.length}</p>
                        <p className="text-[10px] text-gray-600 flex items-center justify-center gap-1"><Eye className="w-3 h-3"/>Traspasos</p>
                      </div>
                      <div className="cursor-pointer hover:bg-green-100 rounded p-1 transition-colors" onClick={()=>setShowValuePanel(true)}>
                        <p className="text-xl font-bold text-green-600">${transfers.reduce((s,t)=>s+(t.total_cost||0),0).toLocaleString('es-MX',{minimumFractionDigits:2,maximumFractionDigits:2})}</p>
                        <p className="text-[10px] text-gray-600 flex items-center justify-center gap-1"><BarChart3 className="w-3 h-3"/>Valor Total</p>
                      </div>
                      <div className="cursor-pointer hover:bg-blue-100 rounded p-1 transition-colors" onClick={()=>{const c={};transfers.forEach(t=>{c[t.from_area_name]=(c[t.from_area_name]||0)+1;});const top=Object.entries(c).sort((a,b)=>b[1]-a[1])[0];if(top)setShowAreaPanel({type:'from',name:top[0]});}}>
                        <p className="text-sm font-bold text-blue-600 truncate">{(()=>{const c={};transfers.forEach(t=>{c[t.from_area_name]=(c[t.from_area_name]||0)+1;});return Object.entries(c).sort((a,b)=>b[1]-a[1])[0]?.[0]||'-';})()}</p>
                        <p className="text-[10px] text-gray-600 flex items-center justify-center gap-1"><Eye className="w-3 h-3"/>Más Envía</p>
                      </div>
                      <div className="cursor-pointer hover:bg-purple-100 rounded p-1 transition-colors" onClick={()=>{const c={};transfers.forEach(t=>{c[t.to_area_name]=(c[t.to_area_name]||0)+1;});const top=Object.entries(c).sort((a,b)=>b[1]-a[1])[0];if(top)setShowAreaPanel({type:'to',name:top[0]});}}>
                        <p className="text-sm font-bold text-purple-600 truncate">{(()=>{const c={};transfers.forEach(t=>{c[t.to_area_name]=(c[t.to_area_name]||0)+1;});return Object.entries(c).sort((a,b)=>b[1]-a[1])[0]?.[0]||'-';})()}</p>
                        <p className="text-[10px] text-gray-600 flex items-center justify-center gap-1"><Eye className="w-3 h-3"/>Más Recibe</p>
                      </div>
                    </div>
                  )}
                  {/* Top 10 - Clickeable - Ahora abre Dashboard completo */}
                  {transfers.length > 0 && (
                    <div 
                      className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:shadow-md transition-all"
                      onClick={() => setShowProductsDashboard(true)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-gray-700 flex items-center gap-2">
                          <Package className="w-4 h-4 text-orange-500" />
                          Productos Trasladados
                        </span>
                        <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                          Clic para ver todos →
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 text-xs">
                        {(()=>{
                          const c={};
                          transfers.forEach(t=>{const n=t.supply_name||t.supply_id;c[n]=(c[n]||0)+t.quantity;});
                          const sorted = Object.entries(c).sort((a,b)=>b[1]-a[1]);
                          const top5 = sorted.slice(0,5);
                          const remaining = sorted.length - 5;
                          return (
                            <>
                              {top5.map(([n,q],i)=>(
                                <span key={n} className="bg-white px-2 py-1 rounded border shadow-sm">
                                  <span className="text-orange-600 font-bold">{i+1}.</span> {n}: <span className="font-semibold">{q.toFixed(1)}</span>
                                </span>
                              ))}
                              {remaining > 0 && (
                                <span className="bg-orange-500 text-white px-2 py-1 rounded font-medium">
                                  +{remaining} más
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {/* Alertas de tendencia */}
                  {transfers.length > 5 && (()=>{
                    const now = new Date();
                    const halfPeriod = Math.floor(transfers.length / 2);
                    const recent = transfers.slice(0, halfPeriod);
                    const older = transfers.slice(halfPeriod);
                    const recentValue = recent.reduce((s,t)=>s+(t.total_cost||0),0);
                    const olderValue = older.reduce((s,t)=>s+(t.total_cost||0),0);
                    const change = olderValue > 0 ? ((recentValue - olderValue) / olderValue * 100) : 0;
                    if (Math.abs(change) > 20) {
                      return (
                        <div className={`p-2 rounded-lg border text-xs flex items-center gap-2 ${change > 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
                          <AlertCircle className="w-4 h-4" />
                          <span><strong>Alerta:</strong> Los traspasos {change > 0 ? 'aumentaron' : 'disminuyeron'} {Math.abs(change).toFixed(0)}% comparado con el período anterior</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {/* Export Buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if(transfers.length===0){toast.error('No hay datos');return;}
                        const c={};transfers.forEach(t=>{const n=t.supply_name||t.supply_id;c[n]=(c[n]||0)+t.quantity;});
                        const top10=Object.entries(c).sort((a,b)=>b[1]-a[1]).slice(0,10);
                        const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Traspasos</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11px;padding:15px}.header{text-align:center;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:15px}.stats{display:flex;justify-content:space-around;margin-bottom:15px;padding:10px;background:#f5f5f5;border-radius:5px}.stat{text-align:center}.stat-value{font-size:14px;font-weight:bold}table{width:100%;border-collapse:collapse;font-size:10px}th{background:#f3f4f6;padding:6px;border:1px solid #ddd}td{padding:5px;border:1px solid #ddd}.top10{margin:15px 0;padding:10px;background:#fff8e1;border-radius:5px}.branch{font-size:9px;color:#666}</style></head><body><div class="header"><h1>${branchName||'RESTAURANTE'}</h1><p>HISTORIAL DE TRASPASOS</p><p>${transferDateFrom||''}${transferDateFrom&&transferDateTo?' - ':''}${transferDateTo||new Date().toLocaleDateString('es-MX')}</p></div><div class="stats"><div class="stat"><div class="stat-value">${transfers.length}</div><div>Traspasos</div></div><div class="stat"><div class="stat-value">$${transfers.reduce((s,t)=>s+(t.total_cost||0),0).toFixed(2)}</div><div>Valor Total</div></div></div><div class="top10"><strong>Top 10 Insumos:</strong> ${top10.map(([n,q],i)=>`${i+1}.${n}:${q.toFixed(1)}`).join(' | ')}</div><table><thead><tr><th>Fecha</th><th>Insumo</th><th>Cant</th><th>Origen</th><th>Destino</th><th>Costo</th></tr></thead><tbody>${transfers.map(t=>{const loc=getTransferLocationInfo(t);return `<tr><td>${new Date(t.created_at).toLocaleDateString('es-MX')}</td><td>${t.supply_name||t.supply_id}</td><td>${t.quantity}</td><td>${t.from_area_name||'-'}${loc.fromBranchName?`<br><span class="branch">${loc.fromBranchName}</span>`:''}</td><td>${t.to_area_name||'-'}${loc.toBranchName?`<br><span class="branch">${loc.toBranchName}</span>`:''}</td><td>$${(t.total_cost||0).toFixed(2)}</td></tr>`;}).join('')}</tbody></table></body></html>`;
                        const container=document.createElement('div');container.innerHTML=html;document.body.appendChild(container);
                        html2pdf().set({margin:10,filename:`traspasos_${new Date().toISOString().slice(0,10)}.pdf`,html2canvas:{scale:2},jsPDF:{format:'a4'}}).from(container).save().then(()=>{document.body.removeChild(container);toast.success('PDF descargado');});
                      }}
                    >
                      <FileDown className="w-4 h-4 mr-1" />PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if(transfers.length===0){toast.error('No hay datos');return;}
                        const h=['Fecha','Hora','Insumo','Cantidad','Unidad','Origen','Sucursal Origen','Destino','Sucursal Destino','Costo'];
                        const r=transfers.map(t=>{const loc=getTransferLocationInfo(t);return [new Date(t.created_at).toLocaleDateString('es-MX'),new Date(t.created_at).toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'}),t.supply_name||t.supply_id,t.quantity,t.unit||'',t.from_area_name||'',loc.fromBranchName||'',t.to_area_name||'',loc.toBranchName||'',(t.total_cost||0).toFixed(2)];});
                        const csv=[h,...r].map(row=>row.map(c=>`"${c}"`).join(',')).join('\n');
                        const blob=new Blob(['\ufeff'+csv],{type:'text/csv;charset=utf-8;'});
                        const link=document.createElement('a');link.href=URL.createObjectURL(blob);link.download=`traspasos_${new Date().toISOString().slice(0,10)}.csv`;link.click();
                        toast.success('Excel descargado');
                      }}
                    >
                      <FileText className="w-4 h-4 mr-1" />Excel
                    </Button>
                    {Object.keys(selectedTransfers).filter(k => selectedTransfers[k]).length > 0 && (
                      <Button
                        size="sm"
                        onClick={() => {
                          const selectedKeys = Object.keys(selectedTransfers).filter(k => selectedTransfers[k]);
                          if (selectedKeys.length === 0) {
                            toast.error('Selecciona al menos un traspaso');
                            return;
                          }
                          // Get grouped data
                          const grouped = {};
                          transfers.forEach(t => {
                            const key = t.batch_id || `${t.created_at?.slice(0,16)}_${t.from_area_id}_${t.to_area_id}_${t.created_by}`;
                            if (!grouped[key]) {
                              grouped[key] = {
                                key,
                                created_at: t.created_at,
                                from_area_name: t.from_area_name,
                                to_area_name: t.to_area_name,
                                created_by: t.created_by,
                                items: []
                              };
                            }
                            grouped[key].items.push(t);
                          });
                          
                          const selectedGroups = selectedKeys.map(k => grouped[k]).filter(Boolean);
                          
                          const printContent = `
                            <!DOCTYPE html>
                            <html>
                            <head>
                              <meta charset="UTF-8">
                              <title>Vales de Traspaso</title>
                              <style>
                                * { margin: 0; padding: 0; box-sizing: border-box; }
                                body { font-family: Arial, sans-serif; font-size: 11px; padding: 15px; }
                                .vale { page-break-after: always; margin-bottom: 30px; border: 1px solid #ddd; padding: 15px; }
                                .vale:last-child { page-break-after: avoid; }
                                .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
                                .header h1 { font-size: 16px; }
                                .header p { font-size: 11px; color: #666; }
                                .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; padding: 8px; background: #f5f5f5; border-radius: 4px; font-size: 10px; }
                                table { width: 100%; border-collapse: collapse; font-size: 10px; }
                                th { background: #333; color: white; padding: 6px; text-align: left; }
                                td { padding: 5px 6px; border-bottom: 1px solid #ddd; }
                                .total-row { font-weight: bold; background: #e8f4fd; }
                                .signatures { display: flex; justify-content: space-between; margin-top: 25px; }
                                .signature-box { text-align: center; width: 30%; }
                                .signature-line { border-bottom: 1px solid #333; height: 30px; margin-bottom: 3px; }
                                .signature-label { font-size: 9px; }
                              </style>
                            </head>
                            <body>
                              ${selectedGroups.map(group => {
                                const totalValue = group.items.reduce((sum, t) => sum + (t.total_cost || 0), 0);
                                return `
                                  <div class="vale">
                                    <div class="header">
                                      <h1>${branchName || 'RESTAURANTE'}</h1>
                                      <p>VALE DE TRASPASO</p>
                                    </div>
                                    <div class="info-row">
                                      <span><strong>Fecha:</strong> ${new Date(group.created_at).toLocaleDateString('es-MX')}</span>
                                      <span><strong>Hora:</strong> ${new Date(group.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
                                      <span><strong>Origen:</strong> ${group.from_area_name}</span>
                                      <span><strong>Destino:</strong> ${group.to_area_name}</span>
                                      <span><strong>Usuario:</strong> ${group.created_by || 'Sistema'}</span>
                                    </div>
                                    <table>
                                      <thead><tr><th>#</th><th>Insumo</th><th style="text-align:right">Cantidad</th><th style="text-align:right">Total</th></tr></thead>
                                      <tbody>
                                        ${group.items.map((item, idx) => `
                                          <tr>
                                            <td>${idx + 1}</td>
                                            <td>${item.supply_name || item.supply_id}</td>
                                            <td style="text-align:right">${item.quantity} ${item.unit || ''}</td>
                                            <td style="text-align:right">$${(item.total_cost || 0).toFixed(2)}</td>
                                          </tr>
                                        `).join('')}
                                        <tr class="total-row"><td colspan="2">TOTAL</td><td style="text-align:right">${group.items.length} items</td><td style="text-align:right">$${totalValue.toFixed(2)}</td></tr>
                                      </tbody>
                                    </table>
                                    <div class="signatures">
                                      <div class="signature-box"><div class="signature-line"></div><div class="signature-label">Entregó</div></div>
                                      <div class="signature-box"><div class="signature-line"></div><div class="signature-label">Recibió</div></div>
                                      <div class="signature-box"><div class="signature-line"></div><div class="signature-label">Autorizó</div></div>
                                    </div>
                                  </div>
                                `;
                              }).join('')}
                            </body>
                            </html>
                          `;
                          // Create temp element and download PDF
                          const container = document.createElement('div');
                          container.innerHTML = printContent;
                          document.body.appendChild(container);
                          
                          html2pdf().set({
                            margin: 10,
                            filename: `traspasos_${new Date().toISOString().slice(0,10)}.pdf`,
                            image: { type: 'jpeg', quality: 0.98 },
                            html2canvas: { scale: 2 },
                            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
                          }).from(container).save().then(() => {
                            document.body.removeChild(container);
                            toast.success('PDF descargado');
                          });
                        }}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <FileDown className="w-4 h-4 mr-1" />
                        PDF ({Object.keys(selectedTransfers).filter(k => selectedTransfers[k]).length})
                      </Button>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {transfers.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ArrowRightLeft className="w-16 h-16 mx-auto mb-3 opacity-30" />
                    <p className="text-lg font-medium">No hay traspasos registrados</p>
                    <p className="text-sm text-gray-400 mb-4">Los movimientos entre áreas aparecerán aquí</p>
                    <Button 
                      onClick={() => {
                        setTransferForm({ from_area_id: '', to_area_id: '', supply_id: '', supply_search: '', quantity: '', reason: 'reabastecimiento', notes: '' });
                        setTransferSourceStock([]);
                        setTransferSourceProcessed([]);
                        setTransferItems([]);
                        setTransferModalOpen(true);
                      }}
                      data-testid="create-first-transfer"
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Crear primer traspaso
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {/* Group transfers by batch_id or by same minute+origin+destination */}
                    {(() => {
                      // Group transfers using paginated data
                      const grouped = {};
                      paginatedTransfers.forEach(t => {
                        // Use batch_id if available, otherwise group by time+areas
                        const key = t.batch_id || `${t.created_at?.slice(0,16)}_${t.from_area_id}_${t.to_area_id}_${t.created_by}`;
                        if (!grouped[key]) {
                          // Obtener nombres completos con sucursal
                          const locInfo = getTransferLocationInfo(t);
                          grouped[key] = {
                            key,
                            batch_id: t.batch_id,
                            created_at: t.created_at,
                            from_area_name: t.from_area_name,
                            to_area_name: t.to_area_name,
                            from_full: locInfo.fromFull,
                            to_full: locInfo.toFull,
                            from_branch_name: locInfo.fromBranchName,
                            to_branch_name: locInfo.toBranchName,
                            from_branch_id: t.from_branch_id,
                            to_branch_id: t.to_branch_id,
                            created_by: t.created_by,
                            reason: t.reason,
                            items: []
                          };
                        }
                        grouped[key].items.push(t);
                      });
                      
                      return Object.values(grouped).map((group) => {
                        const isExpanded = expandedBatches[group.key];
                        const date = new Date(group.created_at);
                        const isToday = new Date().toDateString() === date.toDateString();
                        const totalValue = group.items.reduce((sum, t) => sum + (t.total_cost || 0), 0);
                        const isSelected = selectedTransfers[group.key] || false;
                        
                        return (
                          <div key={group.key} className="bg-white">
                            {/* Accordion Header */}
                            <div 
                              className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-orange-50/50 transition-colors ${isExpanded ? 'bg-orange-50' : ''} ${isSelected ? 'bg-green-50' : ''}`}
                              onClick={() => setExpandedBatches(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                            >
                              <div className="flex items-center gap-4">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setSelectedTransfers(prev => ({ ...prev, [group.key]: !prev[group.key] }));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                                />
                                <button className="p-1 hover:bg-gray-200 rounded transition-transform">
                                  <ChevronRight className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                </button>
                                <div className="flex flex-col">
                                  <span className={`font-semibold ${isToday ? 'text-orange-600' : 'text-gray-900'}`}>
                                    {date.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                                  </span>
                                  <span className="text-sm text-gray-500 font-mono">
                                    {date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="flex flex-col">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                                      {group.from_area_name}
                                    </span>
                                    {group.from_branch_name && (
                                      <span className="text-xs text-blue-500 mt-0.5 text-center">{group.from_branch_name}</span>
                                    )}
                                  </div>
                                  <ArrowRight className="w-4 h-4 text-gray-400" />
                                  <div className="flex flex-col">
                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
                                      {group.to_area_name}
                                    </span>
                                    {group.to_branch_name && (
                                      <span className="text-xs text-green-500 mt-0.5 text-center">{group.to_branch_name}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-6">
                                <div className="text-center">
                                  <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-bold">
                                    {group.items.length} {group.items.length === 1 ? 'insumo' : 'insumos'}
                                  </span>
                                </div>
                                <div className="text-right min-w-[100px]">
                                  <span className="font-bold text-gray-900">${totalValue.toFixed(2)}</span>
                                </div>
                                <div className="flex items-center gap-2 min-w-[120px]">
                                  <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-white text-xs font-bold">
                                    {(group.created_by || 'S')[0].toUpperCase()}
                                  </div>
                                  <span className="text-sm text-gray-600">{group.created_by || 'Sistema'}</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const printContent = `
                                      <!DOCTYPE html>
                                      <html>
                                      <head>
                                        <meta charset="UTF-8">
                                        <title>Vale de Traspaso</title>
                                        <style>
                                          * { margin: 0; padding: 0; box-sizing: border-box; }
                                          body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; }
                                          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
                                          .header h1 { font-size: 18px; margin-bottom: 5px; }
                                          .header p { font-size: 12px; color: #666; }
                                          .info-row { display: flex; justify-content: space-between; margin-bottom: 15px; padding: 10px; background: #f5f5f5; border-radius: 5px; }
                                          .info-item { text-align: center; }
                                          .info-label { font-size: 10px; color: #666; }
                                          .info-value { font-weight: bold; font-size: 12px; }
                                          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
                                          th { background: #333; color: white; padding: 10px; text-align: left; }
                                          td { padding: 8px 10px; border-bottom: 1px solid #ddd; }
                                          tr:nth-child(even) { background: #f9f9f9; }
                                          .total-row { font-weight: bold; background: #e8f4fd !important; }
                                          .signatures { display: flex; justify-content: space-between; margin-top: 40px; padding-top: 20px; }
                                          .signature-box { text-align: center; width: 30%; }
                                          .signature-line { border-bottom: 1px solid #333; height: 40px; margin-bottom: 5px; }
                                          .signature-label { font-size: 10px; }
                                          @media print { body { padding: 15px; } }
                                        </style>
                                      </head>
                                      <body>
                                        <div class="header">
                                          <h1>${branchName || 'RESTAURANTE'}</h1>
                                          <p>VALE DE TRASPASO</p>
                                        </div>
                                        <div class="info-row">
                                          <div class="info-item">
                                            <div class="info-label">FECHA</div>
                                            <div class="info-value">${new Date(group.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                          </div>
                                          <div class="info-item">
                                            <div class="info-label">HORA</div>
                                            <div class="info-value">${new Date(group.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</div>
                                          </div>
                                          <div class="info-item">
                                            <div class="info-label">ORIGEN</div>
                                            <div class="info-value">${group.from_area_name}${group.from_branch_name ? `<br><small style="color:#666">${group.from_branch_name}</small>` : ''}</div>
                                          </div>
                                          <div class="info-item">
                                            <div class="info-label">DESTINO</div>
                                            <div class="info-value">${group.to_area_name}${group.to_branch_name ? `<br><small style="color:#666">${group.to_branch_name}</small>` : ''}</div>
                                          </div>
                                          <div class="info-item">
                                            <div class="info-label">USUARIO</div>
                                            <div class="info-value">${group.created_by || 'Sistema'}</div>
                                          </div>
                                        </div>
                                        <table>
                                          <thead>
                                            <tr>
                                              <th>#</th>
                                              <th>Insumo</th>
                                              <th style="text-align:right">Cantidad</th>
                                              <th style="text-align:right">Costo Unit.</th>
                                              <th style="text-align:right">Total</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            ${group.items.map((item, idx) => `
                                              <tr>
                                                <td>${idx + 1}</td>
                                                <td>${item.supply_name || item.supply_id}</td>
                                                <td style="text-align:right">${item.quantity} ${item.unit || ''}</td>
                                                <td style="text-align:right">$${(item.cost_per_unit || 0).toFixed(2)}</td>
                                                <td style="text-align:right">$${(item.total_cost || 0).toFixed(2)}</td>
                                              </tr>
                                            `).join('')}
                                            <tr class="total-row">
                                              <td colspan="2">TOTAL</td>
                                              <td style="text-align:right">${group.items.length} items</td>
                                              <td></td>
                                              <td style="text-align:right">$${totalValue.toFixed(2)}</td>
                                            </tr>
                                          </tbody>
                                        </table>
                                        <div class="signatures">
                                          <div class="signature-box">
                                            <div class="signature-line"></div>
                                            <div class="signature-label"><strong>Entregó</strong></div>
                                          </div>
                                          <div class="signature-box">
                                            <div class="signature-line"></div>
                                            <div class="signature-label"><strong>Recibió</strong></div>
                                          </div>
                                          <div class="signature-box">
                                            <div class="signature-line"></div>
                                            <div class="signature-label"><strong>Autorizó</strong></div>
                                          </div>
                                        </div>
                                      </body>
                                      </html>
                                    `;
                                    const container = document.createElement('div');
                                    container.innerHTML = printContent;
                                    document.body.appendChild(container);
                                    
                                    html2pdf().set({
                                      margin: 10,
                                      filename: `vale_traspaso_${new Date(group.created_at).toISOString().slice(0,10)}.pdf`,
                                      image: { type: 'jpeg', quality: 0.98 },
                                      html2canvas: { scale: 2 },
                                      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
                                    }).from(container).save().then(() => {
                                      document.body.removeChild(container);
                                      toast.success('PDF descargado');
                                    });
                                  }}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                                  title="Descargar PDF"
                                >
                                  <FileDown className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Accordion Content - Items Detail */}
                            {isExpanded && (
                              <div className="bg-slate-50 border-t border-b border-slate-200">
                                <table className="w-full">
                                  <thead className="bg-slate-100 text-xs text-slate-600 uppercase">
                                    <tr>
                                      <th className="text-left px-6 py-2 pl-14">Insumo</th>
                                      <th className="text-right px-4 py-2">Cantidad</th>
                                      <th className="text-right px-4 py-2">Costo Unit.</th>
                                      <th className="text-right px-4 py-2">Total</th>
                                      <th className="text-center px-4 py-2">Acciones</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-200">
                                    {group.items.map((item, idx) => (
                                      <tr key={item.transfer_id} className="hover:bg-slate-100/50">
                                        <td className="px-6 py-2 pl-14">
                                          <div className="flex items-center gap-2">
                                            <span className="text-slate-400">{idx === group.items.length - 1 ? '└─' : '├─'}</span>
                                            <Package className="w-4 h-4 text-slate-400" />
                                            <span className="font-medium text-slate-800">{item.supply_name}</span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                          {editingTransfer === item.transfer_id ? (
                                            <input
                                              type="number"
                                              step="0.01"
                                              value={editTransferForm.quantity}
                                              onChange={(e) => setEditTransferForm(prev => ({ ...prev, quantity: e.target.value }))}
                                              className="w-20 px-2 py-1 border rounded text-right"
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          ) : (
                                            <span className="font-mono font-bold text-slate-800">
                                              {item.quantity} <span className="text-slate-500 font-normal">{item.unit}</span>
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-right text-slate-600">
                                          ${(item.cost_per_unit || 0).toFixed(2)}
                                        </td>
                                        <td className="px-4 py-2 text-right font-semibold text-slate-800">
                                          ${(item.total_cost || 0).toFixed(2)}
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                          {editingTransfer === item.transfer_id ? (
                                            <div className="flex items-center justify-center gap-1">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  try {
                                                    await api.put(`/inventory/transfers/${item.transfer_id}`, {
                                                      quantity: parseFloat(editTransferForm.quantity) || item.quantity,
                                                      reason: editTransferForm.reason || item.reason,
                                                      notes: editTransferForm.notes
                                                    });
                                                    toast.success('Traspaso actualizado');
                                                    setEditingTransfer(null);
                                                    // Refresh transfers
                                                    const branchParams = branchId ? `&branch_id=${branchId}&is_matriz=${isMatriz}` : '';
                                                    const res = await api.get(`/inventory/transfers?days=90${branchParams}`);
                                                    setTransfers(res.data);
                                                  } catch (error) {
                                                    toast.error('Error al actualizar');
                                                  }
                                                }}
                                                className="text-green-600 hover:bg-green-100"
                                              >
                                                <Check className="w-4 h-4" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setEditingTransfer(null);
                                                }}
                                                className="text-red-600 hover:bg-red-100"
                                              >
                                                <X className="w-4 h-4" />
                                              </Button>
                                            </div>
                                          ) : (
                                            <div className="flex items-center gap-1">
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setEditingTransfer(item.transfer_id);
                                                  setEditTransferForm({
                                                    quantity: item.quantity.toString(),
                                                    reason: item.reason || '',
                                                    notes: item.notes || ''
                                                  });
                                                }}
                                                className="text-blue-600 hover:bg-blue-100"
                                                title="Editar"
                                              >
                                                <Pencil className="w-4 h-4" />
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  if (deletingTransfer === item.transfer_id) return;
                                                  if (!window.confirm(`¿Eliminar traspaso de ${item.quantity} ${item.unit} de ${item.supply_name}? El stock será revertido.`)) return;
                                                  setDeletingTransfer(item.transfer_id);
                                                  try {
                                                    await api.delete(`/inventory/transfers/${item.transfer_id}`);
                                                    toast.success('Traspaso eliminado');
                                                    // Refresh transfers
                                                    const branchParams = branchId ? `&branch_id=${branchId}&is_matriz=${isMatriz}` : '';
                                                    const res = await api.get(`/inventory/transfers?days=90${branchParams}`);
                                                    setTransfers(res.data);
                                                  } catch (error) {
                                                    toast.error('Error al eliminar');
                                                  } finally {
                                                    setDeletingTransfer(null);
                                                  }
                                                }}
                                                className="text-red-600 hover:bg-red-100"
                                                title="Eliminar"
                                                disabled={deletingTransfer === item.transfer_id}
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </Button>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot className="bg-slate-100 font-semibold text-slate-700">
                                    <tr>
                                      <td colSpan="3" className="px-6 py-2 pl-14 text-right">Total del traspaso:</td>
                                      <td className="px-4 py-2 text-right text-lg">${totalValue.toFixed(2)}</td>
                                      <td></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                    
                    {/* Summary Footer with Pagination */}
                    <div className="border-t bg-slate-50 px-4 py-3">
                      {/* Pagination Controls */}
                      {totalTransfersPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mb-3 pb-3 border-b border-slate-200">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setTransfersPage(1)}
                            disabled={transfersPage === 1}
                            className="text-xs"
                          >
                            Primera
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setTransfersPage(p => Math.max(1, p - 1))}
                            disabled={transfersPage === 1}
                          >
                            <ChevronRight className="w-4 h-4 rotate-180" />
                          </Button>
                          
                          <div className="flex items-center gap-1">
                            {/* Page numbers */}
                            {Array.from({ length: Math.min(5, totalTransfersPages) }, (_, i) => {
                              let pageNum;
                              if (totalTransfersPages <= 5) {
                                pageNum = i + 1;
                              } else if (transfersPage <= 3) {
                                pageNum = i + 1;
                              } else if (transfersPage >= totalTransfersPages - 2) {
                                pageNum = totalTransfersPages - 4 + i;
                              } else {
                                pageNum = transfersPage - 2 + i;
                              }
                              return (
                                <Button
                                  key={pageNum}
                                  variant={pageNum === transfersPage ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setTransfersPage(pageNum)}
                                  className={`w-8 h-8 p-0 text-xs ${pageNum === transfersPage ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setTransfersPage(p => Math.min(totalTransfersPages, p + 1))}
                            disabled={transfersPage === totalTransfersPages}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setTransfersPage(totalTransfersPages)}
                            disabled={transfersPage === totalTransfersPages}
                            className="text-xs"
                          >
                            Última
                          </Button>
                          
                          <span className="text-sm text-slate-500 ml-2">
                            Página <strong>{transfersPage}</strong> de <strong>{totalTransfersPages}</strong>
                          </span>
                        </div>
                      )}
                      
                      {/* Stats Row */}
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4">
                          <span className="text-gray-500">
                            Mostrando: <strong className="text-gray-900">{paginatedTransfers.length}</strong> de <strong>{filteredTransfers.length}</strong>
                            {filteredTransfers.length !== transfers.length && (
                              <span className="text-slate-400"> (filtrado de {transfers.length})</span>
                            )}
                          </span>
                          <span className="text-gray-300">|</span>
                          <span className="text-gray-500">
                            Hoy: <strong className="text-orange-600">
                              {transfers.filter(t => new Date(t.created_at).toDateString() === new Date().toDateString()).length}
                            </strong>
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setTransferForm({ from_area_id: '', to_area_id: '', supply_id: '', supply_search: '', quantity: '', reason: 'reabastecimiento', notes: '' });
                            setTransferSourceStock([]);
                            setTransferSourceProcessed([]);
                            setTransferItems([]);
                            setTransferModalOpen(true);
                          }}
                          className="border-orange-200 text-orange-600 hover:bg-orange-50"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Nuevo Traspaso
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* TAB: Reportes */}
          {activeTab === 'reports' && (
            <>
              {/* Report Type Selector */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex gap-2">
                  <Button
                    variant={reportType === 'consumption' ? 'default' : 'outline'}
                    onClick={() => setReportType('consumption')}
                    data-testid="report-consumption"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Consumo por Producto
                  </Button>
                  <Button
                    variant={reportType === 'movements' ? 'default' : 'outline'}
                    onClick={() => setReportType('movements')}
                    data-testid="report-movements"
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Movimientos
                  </Button>
                  <Button
                    variant={reportType === 'purchases' ? 'default' : 'outline'}
                    onClick={() => setReportType('purchases')}
                    data-testid="report-purchases"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Compras
                  </Button>
                </div>
              </div>
              
              {/* Nuevo selector de período mejorado */}
              <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant={reportPeriodType === 'hoy' ? 'default' : 'outline'}
                    onClick={() => setReportPeriodType('hoy')}
                    className={reportPeriodType === 'hoy' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    Hoy
                  </Button>
                  <Button
                    size="sm"
                    variant={reportPeriodType === 'semana' ? 'default' : 'outline'}
                    onClick={() => setReportPeriodType('semana')}
                    className={reportPeriodType === 'semana' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    Semana
                  </Button>
                  <Button
                    size="sm"
                    variant={reportPeriodType === 'mes' ? 'default' : 'outline'}
                    onClick={() => setReportPeriodType('mes')}
                    className={reportPeriodType === 'mes' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    Mes
                  </Button>
                  <Button
                    size="sm"
                    variant={reportPeriodType === 'todo' ? 'default' : 'outline'}
                    onClick={() => setReportPeriodType('todo')}
                    className={reportPeriodType === 'todo' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    Todo
                  </Button>
                </div>
                
                <div className="h-6 w-px bg-gray-300 mx-1" />
                
                <div className="flex items-center gap-2">
                  <select
                    value={reportPeriodType === 'month-specific' ? reportSelectedMonth : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        setReportSelectedMonth(e.target.value);
                        setReportPeriodType('month-specific');
                      }
                    }}
                    className="border rounded-md px-2 py-1.5 text-sm bg-white"
                  >
                    <option value="">Mes específico</option>
                    {getMonthOptions().map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="h-6 w-px bg-gray-300 mx-1" />
                
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Personalizado:</span>
                  <Input
                    type="date"
                    value={reportCustomStart}
                    onChange={(e) => {
                      setReportCustomStart(e.target.value);
                      if (e.target.value && reportCustomEnd) setReportPeriodType('custom');
                    }}
                    className="w-40 h-8 text-sm"
                  />
                  <span className="text-gray-400">a</span>
                  <Input
                    type="date"
                    value={reportCustomEnd}
                    onChange={(e) => {
                      setReportCustomEnd(e.target.value);
                      if (reportCustomStart && e.target.value) setReportPeriodType('custom');
                    }}
                    className="w-40 h-8 text-sm"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      if (reportCustomStart && reportCustomEnd) {
                        setReportPeriodType('custom');
                        fetchReport(reportType);
                      }
                    }}
                    className="bg-orange-500 hover:bg-orange-600 h-8"
                  >
                    Buscar
                  </Button>
                </div>
              </div>
              
              {/* Indicador de período activo */}
              <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                <Calendar className="w-4 h-4 text-orange-600" />
                <span className="text-sm font-medium text-orange-800">
                  📊 Mostrando: {getReportDateRange().label}
                </span>
              </div>

              {loadingReport ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                </div>
              ) : (
                <>
                  {/* Consumption by Product Report */}
                  {reportType === 'consumption' && consumptionReport && (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <ClipboardList className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Órdenes Completadas</p>
                                <p className="text-2xl font-bold">{consumptionReport.total_orders}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-green-100 rounded-lg">
                                <ShoppingCart className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Productos Vendidos</p>
                                <p className="text-2xl font-bold">{consumptionReport.products_sold?.length || 0}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-orange-100 rounded-lg">
                                <Package className="w-5 h-5 text-orange-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Insumos Consumidos</p>
                                <p className="text-2xl font-bold">{consumptionReport.supplies_consumed?.length || 0}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Products Sold Table */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Consumo de Insumos por Producto Vendido</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {consumptionReport.products_sold?.length === 0 ? (
                            <p className="text-center py-8 text-gray-500">
                              No hay ventas completadas en este período
                            </p>
                          ) : (
                            <div className="space-y-4">
                              {consumptionReport.products_sold?.map((product, idx) => (
                                <div key={idx} className="border rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <h4 className="font-semibold">{product.product_name}</h4>
                                      <p className="text-sm text-gray-500">
                                        Vendido {product.times_sold} veces
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-mono font-bold text-green-600">
                                        ${product.total_revenue?.toFixed(2)}
                                      </p>
                                      <p className="text-xs text-gray-500">Ingresos</p>
                                    </div>
                                  </div>
                                  {product.recipe_ingredients?.length > 0 && (
                                    <div className="bg-gray-50 rounded-md p-3">
                                      <p className="text-xs font-medium text-gray-600 mb-2">
                                        Insumos consumidos:
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {product.recipe_ingredients.map((ing, i) => (
                                          <span
                                            key={i}
                                            className="text-xs bg-white px-2 py-1 rounded border"
                                          >
                                            {ing.supply_name}: <span className="font-mono">{ing.total_used} {ing.unit}</span>
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Supplies Summary */}
                      {consumptionReport.supplies_consumed?.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle>Resumen de Insumos Consumidos</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left p-3 text-sm font-medium text-gray-600">Insumo</th>
                                    <th className="text-right p-3 text-sm font-medium text-gray-600">Total Consumido</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {consumptionReport.supplies_consumed.map((supply, idx) => (
                                    <tr key={idx} className="border-t">
                                      <td className="p-3">{supply.supply_name}</td>
                                      <td className="p-3 text-right font-mono">
                                        {supply.total_consumed?.toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Movements Report */}
                  {reportType === 'movements' && (
                    <Card>
                      <CardHeader className="pb-2">
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                              <ArrowRightLeft className="w-5 h-5 text-orange-500" />
                              Historial de Movimientos
                            </CardTitle>
                            <span className="text-sm text-gray-500">
                              {movementsReport.length} movimientos
                            </span>
                          </div>
                          
                          {/* Filtros de flujo */}
                          <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm font-medium text-gray-600">Flujo:</span>
                            {[
                              { id: 'all', label: 'Todos' },
                              { id: 'within_matriz', label: 'Dentro de Matriz' },
                              { id: 'to_branches', label: 'Matriz → Sucursales' },
                              { id: 'from_branches', label: 'Sucursales → Matriz' }
                            ].map(f => (
                              <button
                                key={f.id}
                                onClick={() => setMovementFlowFilter(f.id)}
                                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                  movementFlowFilter === f.id
                                    ? 'bg-orange-500 text-white'
                                    : 'bg-white border text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {f.label}
                              </button>
                            ))}
                            
                            <div className="h-5 w-px bg-gray-300 mx-2" />
                            
                            {/* Selectores específicos */}
                            <select
                              value={movementOriginBranch}
                              onChange={(e) => setMovementOriginBranch(e.target.value)}
                              className="text-xs border rounded px-2 py-1 bg-white"
                            >
                              <option value="">Origen: Todos</option>
                              {allBranches.map(b => (
                                <option key={b.branch_id} value={b.branch_id}>
                                  Origen: {b.name}
                                </option>
                              ))}
                            </select>
                            
                            <select
                              value={movementDestBranch}
                              onChange={(e) => setMovementDestBranch(e.target.value)}
                              className="text-xs border rounded px-2 py-1 bg-white"
                            >
                              <option value="">Destino: Todos</option>
                              {allBranches.map(b => (
                                <option key={b.branch_id} value={b.branch_id}>
                                  Destino: {b.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          {/* Buscador de productos */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Buscar producto por nombre..."
                              value={movementSearchTerm}
                              onChange={(e) => setMovementSearchTerm(e.target.value)}
                              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                            {movementSearchTerm && (
                              <button
                                onClick={() => setMovementSearchTerm('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {movementsReport.length === 0 ? (
                          <div className="text-center py-12">
                            <ArrowRightLeft className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">No hay movimientos en este período</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {(() => {
                              // Filtrar movimientos según filtros seleccionados
                              const matrizBranch = allBranches.find(b => b.is_matriz);
                              const matrizId = matrizBranch?.branch_id;
                              
                              let filteredMovements = movementsReport.filter(mov => {
                                // Filtro por flujo
                                if (movementFlowFilter === 'within_matriz') {
                                  return mov.from_branch_id === matrizId && mov.to_branch_id === matrizId;
                                } else if (movementFlowFilter === 'to_branches') {
                                  return mov.from_branch_id === matrizId && mov.to_branch_id !== matrizId;
                                } else if (movementFlowFilter === 'from_branches') {
                                  return mov.from_branch_id !== matrizId && mov.to_branch_id === matrizId;
                                }
                                return true;
                              });
                              
                              // Filtro por origen específico
                              if (movementOriginBranch) {
                                filteredMovements = filteredMovements.filter(mov => 
                                  mov.from_branch_id === movementOriginBranch
                                );
                              }
                              
                              // Filtro por destino específico
                              if (movementDestBranch) {
                                filteredMovements = filteredMovements.filter(mov => 
                                  mov.to_branch_id === movementDestBranch
                                );
                              }
                              
                              if (filteredMovements.length === 0) {
                                return (
                                  <div className="text-center py-8 text-gray-500">
                                    No hay movimientos con los filtros seleccionados
                                  </div>
                                );
                              }
                              
                              // Agrupar por producto
                              const grouped = {};
                              filteredMovements.forEach(mov => {
                                const key = mov.supply_id || mov.supply_name;
                                if (!grouped[key]) {
                                  grouped[key] = {
                                    supply_name: mov.supply_name,
                                    supply_id: mov.supply_id,
                                    unit: mov.unit,
                                    is_elaborado: mov.is_elaborado,
                                    is_consumable: mov.is_consumable,
                                    total_quantity: 0,
                                    total_cost: 0,
                                    movements: []
                                  };
                                }
                                grouped[key].total_quantity += mov.quantity || 0;
                                grouped[key].total_cost += mov.total_cost || 0;
                                grouped[key].movements.push(mov);
                              });
                              
                              // Ordenar por cantidad total
                              let sortedProducts = Object.values(grouped).sort((a, b) => b.total_quantity - a.total_quantity);
                              
                              // Filtrar por término de búsqueda
                              if (movementSearchTerm.trim()) {
                                const searchLower = movementSearchTerm.toLowerCase().trim();
                                sortedProducts = sortedProducts.filter(p => 
                                  p.supply_name?.toLowerCase().includes(searchLower)
                                );
                              }
                              
                              if (sortedProducts.length === 0) {
                                return (
                                  <div className="text-center py-8 text-gray-500">
                                    {movementSearchTerm ? 
                                      `No se encontró "${movementSearchTerm}" en los movimientos` : 
                                      'No hay movimientos con los filtros seleccionados'
                                    }
                                  </div>
                                );
                              }
                              
                              return sortedProducts.map((product, idx) => (
                                <div key={idx} className="border rounded-lg overflow-hidden">
                                  {/* Header del producto - clickeable */}
                                  <div 
                                    className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => setExpandedMovementProducts(prev => ({
                                      ...prev,
                                      [product.supply_id]: !prev[product.supply_id]
                                    }))}
                                  >
                                    <div className="flex items-center gap-3">
                                      {expandedMovementProducts[product.supply_id] ? (
                                        <ChevronDown className="w-5 h-5 text-orange-500" />
                                      ) : (
                                        <ChevronRight className="w-5 h-5 text-gray-400" />
                                      )}
                                      <div>
                                        <span className="font-semibold">{product.supply_name}</span>
                                        {product.is_elaborado && (
                                          <span className="ml-2 bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded-full">
                                            Elaborado
                                          </span>
                                        )}
                                        {product.is_consumable && (
                                          <span className="ml-2 bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0.5 rounded-full">
                                            Consumible
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <span className="text-sm text-gray-500">{product.movements.length} traspasos</span>
                                      <span className="font-mono font-bold text-orange-600">
                                        {product.total_quantity.toFixed(2)} {product.unit}
                                      </span>
                                      <span className="font-mono font-bold text-green-600">
                                        ${product.total_cost.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Detalle de movimientos - expandible */}
                                  {expandedMovementProducts[product.supply_id] && (
                                    <div className="border-t">
                                      <table className="w-full text-sm">
                                        <thead className="bg-blue-50">
                                          <tr>
                                            <th className="text-left p-2 pl-10 text-xs font-medium text-gray-600">Fecha</th>
                                            <th className="text-left p-2 text-xs font-medium text-gray-600">Origen</th>
                                            <th className="text-center p-2 text-xs font-medium text-gray-600">→</th>
                                            <th className="text-left p-2 text-xs font-medium text-gray-600">Destino</th>
                                            <th className="text-right p-2 text-xs font-medium text-gray-600">Cantidad</th>
                                            <th className="text-right p-2 text-xs font-medium text-gray-600">Costo</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {product.movements.map((mov, mIdx) => {
                                            const fromBranch = mov.from_branch_id ? allBranches.find(b => b.branch_id === mov.from_branch_id) : null;
                                            const toBranch = mov.to_branch_id ? allBranches.find(b => b.branch_id === mov.to_branch_id) : null;
                                            return (
                                              <tr key={mIdx} className="border-t hover:bg-gray-50">
                                                <td className="p-2 pl-10 text-gray-600">
                                                  {new Date(mov.created_at).toLocaleDateString('es-MX', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                  })}
                                                </td>
                                                <td className="p-2">
                                                  <div>
                                                    <span className="font-medium">{mov.from_area_name}</span>
                                                    {fromBranch && (
                                                      <span className="block text-xs text-blue-600">{fromBranch.name}</span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td className="p-2 text-center text-gray-400">→</td>
                                                <td className="p-2">
                                                  <div>
                                                    <span className="font-medium">{mov.to_area_name}</span>
                                                    {toBranch && (
                                                      <span className="block text-xs text-green-600">{toBranch.name}</span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td className="p-2 text-right font-mono text-orange-600">
                                                  {mov.quantity} {mov.unit}
                                                </td>
                                                <td className="p-2 text-right font-mono text-green-600">
                                                  ${(mov.total_cost || 0).toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              ));
                            })()}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Purchases Report */}
                  {reportType === 'purchases' && purchasesReport && (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                <FileText className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Total Compras</p>
                                <p className="text-2xl font-bold">{purchasesReport.total_purchases}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-red-100 rounded-lg">
                                <DollarSign className="w-5 h-5 text-red-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Total Gastado</p>
                                <p className="text-2xl font-bold">${purchasesReport.total_spent?.toFixed(2)}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-green-100 rounded-lg">
                                <Package className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Insumos Comprados</p>
                                <p className="text-2xl font-bold">{purchasesReport.supplies_purchased?.length || 0}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Supplies Purchased */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Insumos Comprados</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {purchasesReport.supplies_purchased?.length === 0 ? (
                            <p className="text-center py-8 text-gray-500">
                              No hay compras en este período
                            </p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="text-left p-3 text-sm font-medium text-gray-600">Insumo</th>
                                    <th className="text-right p-3 text-sm font-medium text-gray-600">Cantidad Total</th>
                                    <th className="text-right p-3 text-sm font-medium text-gray-600">Último Costo</th>
                                    <th className="text-right p-3 text-sm font-medium text-gray-600">Total Gastado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {purchasesReport.supplies_purchased.map((supply, idx) => (
                                    <tr key={idx} className="border-t">
                                      <td className="p-3 font-medium">{supply.supply_name}</td>
                                      <td className="p-3 text-right font-mono">
                                        {supply.total_quantity?.toFixed(2)} {supply.unit}
                                      </td>
                                      <td className="p-3 text-right font-mono">
                                        ${supply.last_unit_cost?.toFixed(2)}
                                      </td>
                                      <td className="p-3 text-right font-mono font-bold text-red-600">
                                        ${supply.total_cost?.toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Recent Purchases */}
                      {purchasesReport.purchases?.length > 0 && (
                        <Card>
                          <CardHeader>
                            <CardTitle>Últimas Compras</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {purchasesReport.purchases.map((purchase, idx) => (
                                <div key={idx} className="border rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-2">
                                    <div>
                                      <p className="font-medium">{purchase.supplier || 'Sin proveedor'}</p>
                                      <p className="text-sm text-gray-500">
                                        {new Date(purchase.created_at).toLocaleDateString('es-MX', {
                                          day: '2-digit',
                                          month: 'long',
                                          year: 'numeric'
                                        })}
                                      </p>
                                    </div>
                                    <p className="font-mono font-bold">${purchase.total?.toFixed(2)}</p>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {purchase.items?.map((item, i) => (
                                      <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                        {item.supply_name}: {item.quantity} {item.unit}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Transfer Modal */}
      <Modal 
        open={transferModalOpen} 
        onClose={() => {
          setTransferModalOpen(false);
          setTransferForm({ from_area_id: '', to_area_id: '', supply_id: '', supply_search: '', quantity: '', reason: 'reabastecimiento', notes: '' });
          setTransferSourceStock([]);
          setTransferSourceProcessed([]);
          setTransferItems([]);
        }} 
        title="Nuevo Traspaso"
        size="lg"
      >
        <form onSubmit={handleTransfer} className="space-y-4">
          {/* Areas Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Área Origen</Label>
              <select
                value={transferForm.from_area_id}
                onChange={(e) => handleTransferFromAreaChange(e.target.value)}
                className="w-full mt-1 border rounded-md p-2"
                required
                data-testid="transfer-from-area"
              >
                <option value="">Seleccionar área origen</option>
                {areas.map(area => (
                  <option key={area.area_id} value={area.area_id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Área Destino</Label>
              <select
                value={transferForm.to_area_id}
                onChange={(e) => setTransferForm(prev => ({ ...prev, to_area_id: e.target.value }))}
                className="w-full mt-1 border rounded-md p-2"
                required={!transferForm.to_branch_id}
                disabled={!!transferForm.to_branch_id}
                data-testid="transfer-to-area"
              >
                <option value="">{transferForm.to_branch_id ? 'Automático (Almacén de sucursal)' : 'Seleccionar área destino'}</option>
                {areas.filter(a => a.area_id !== transferForm.from_area_id).map(area => (
                  <option key={area.area_id} value={area.area_id}>
                    {area.name}
                  </option>
                ))}
              </select>
              {transferForm.to_branch_id && (
                <p className="text-xs text-gray-500 mt-1">Los insumos irán al almacén de la sucursal destino</p>
              )}
            </div>
          </div>

          {/* Branch Destination - Only show if Matriz */}
          {isMatriz && allBranches.length > 1 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-blue-800 font-medium">Sucursal Destino (Traspaso entre sucursales)</Label>
              <select
                value={transferForm.to_branch_id}
                onChange={(e) => setTransferForm(prev => ({ ...prev, to_branch_id: e.target.value }))}
                className="w-full mt-1 border border-blue-300 rounded-md p-2 bg-white"
                data-testid="transfer-to-branch"
              >
                <option value="">Misma sucursal ({branchName})</option>
                {allBranches.filter(b => b.branch_id !== branchId && b.is_active !== false).map(branch => (
                  <option key={branch.branch_id} value={branch.branch_id}>
                    📍 {branch.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-blue-600 mt-1">
                Selecciona una sucursal para enviar insumos desde Matriz
              </p>
            </div>
          )}

          {transferForm.from_area_id && (transferForm.to_area_id || transferForm.to_branch_id) && (
            <>
              {/* Item Type Selector */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => {
                    setTransferItemType('supply');
                    setTransferForm(prev => ({ ...prev, supply_id: '', supply_search: '', quantity: '' }));
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    transferItemType === 'supply' ? 'bg-white shadow text-orange-600' : 'text-gray-600'
                  }`}
                >
                  Insumos ({transferSourceStock.length})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTransferItemType('processed');
                    setTransferForm(prev => ({ ...prev, supply_id: '', supply_search: '', quantity: '' }));
                  }}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    transferItemType === 'processed' ? 'bg-white shadow text-purple-600' : 'text-gray-600'
                  }`}
                >
                  Elaborados ({transferSourceProcessed.length})
                </button>
              </div>

              {/* Add Item Form */}
              <div className="p-3 bg-gray-50 rounded-lg border">
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-6 relative">
                    <Input
                      type="text"
                      placeholder={`Buscar ${transferItemType === 'supply' ? 'insumo' : 'elaborado'}...`}
                      value={transferForm.supply_search || ''}
                      onChange={(e) => setTransferForm(prev => ({ ...prev, supply_search: e.target.value, supply_id: '' }))}
                      className="w-full"
                      data-testid="transfer-supply-search"
                    />
                    {transferForm.supply_search && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                        {transferItemType === 'supply' ? (
                          transferSourceStock
                            .filter(item => item.supply_name?.toLowerCase().includes((transferForm.supply_search || '').toLowerCase()))
                            .slice(0, 10)
                            .map(item => (
                              <button
                                key={item.supply_id}
                                type="button"
                                onClick={() => setTransferForm(prev => ({ 
                                  ...prev, 
                                  supply_id: item.supply_id, 
                                  supply_search: item.supply_name
                                }))}
                                className="w-full text-left p-2 hover:bg-orange-50 border-b text-sm flex justify-between items-center"
                              >
                                <span className="font-medium truncate">{item.supply_name}</span>
                                <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                                  {parseFloat(item.quantity).toFixed(2)} {item.unit}
                                </span>
                              </button>
                            ))
                        ) : (
                          transferSourceProcessed
                            .filter(item => item.name?.toLowerCase().includes((transferForm.supply_search || '').toLowerCase()))
                            .slice(0, 10)
                            .map(item => (
                              <button
                                key={item.processed_id}
                                type="button"
                                onClick={() => setTransferForm(prev => ({ 
                                  ...prev, 
                                  supply_id: item.processed_id, 
                                  supply_search: item.name
                                }))}
                                className="w-full text-left p-2 hover:bg-purple-50 border-b text-sm flex justify-between items-center"
                              >
                                <span className="font-medium truncate">{item.name}</span>
                                <span className="text-xs text-gray-500 ml-2 whitespace-nowrap">
                                  {parseFloat(item.stock_quantity || 0).toFixed(2)} {item.unit}
                                </span>
                              </button>
                            ))
                        )}
                        {((transferItemType === 'supply' && transferSourceStock.filter(item => item.supply_name?.toLowerCase().includes((transferForm.supply_search || '').toLowerCase())).length === 0) ||
                          (transferItemType === 'processed' && transferSourceProcessed.filter(item => item.name?.toLowerCase().includes((transferForm.supply_search || '').toLowerCase())).length === 0)) && (
                          <div className="p-3 text-center text-gray-400 text-sm">
                            No se encontraron {transferItemType === 'supply' ? 'insumos' : 'elaborados'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={transferForm.quantity}
                      onChange={(e) => setTransferForm(prev => ({ ...prev, quantity: e.target.value }))}
                      placeholder="Cantidad"
                      data-testid="transfer-quantity"
                    />
                  </div>
                  <div className="col-span-3">
                    <Button 
                      type="button" 
                      onClick={addTransferItem}
                      className="w-full bg-green-500 hover:bg-green-600"
                      disabled={!transferForm.supply_id || !transferForm.quantity}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </div>
                
                {/* Selected item indicator */}
                {transferForm.supply_id && (
                  <div className={`mt-2 p-2 rounded border flex justify-between items-center ${
                    transferItemType === 'supply' ? 'bg-orange-50 border-orange-200' : 'bg-purple-50 border-purple-200'
                  }`}>
                    <span className={`text-sm font-medium ${transferItemType === 'supply' ? 'text-orange-700' : 'text-purple-700'}`}>
                      {transferForm.supply_search}
                    </span>
                    <span className={`text-xs ${transferItemType === 'supply' ? 'text-orange-600' : 'text-purple-600'}`}>
                      {transferItemType === 'supply' 
                        ? `${parseFloat(transferSourceStock.find(s => s.supply_id === transferForm.supply_id)?.quantity || 0).toFixed(2)} ${transferSourceStock.find(s => s.supply_id === transferForm.supply_id)?.unit || ''} disponibles`
                        : `${parseFloat(transferSourceProcessed.find(p => p.processed_id === transferForm.supply_id)?.stock_quantity || 0).toFixed(2)} ${transferSourceProcessed.find(p => p.processed_id === transferForm.supply_id)?.unit || ''} disponibles`
                      }
                    </span>
                  </div>
                )}
              </div>

              {/* Items Cart */}
              {transferItems.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 flex justify-between items-center">
                    <span>Items a traspasar ({transferItems.length})</span>
                  </div>
                  <div className="divide-y max-h-40 overflow-auto">
                    {transferItems.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            item.type === 'supply' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {item.type === 'supply' ? 'INS' : 'ELAB'}
                          </span>
                          <span className="text-sm font-medium">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-gray-600">{item.quantity.toFixed(2)} {item.unit}</span>
                          <button
                            type="button"
                            onClick={() => removeTransferItem(idx)}
                            className="p-1 hover:bg-red-100 rounded text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Motivo del Traspaso */}
          {transferForm.from_area_id && transferForm.to_area_id && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div>
                <Label className="text-blue-700">Motivo del Traspaso</Label>
                <select
                  value={transferForm.reason}
                  onChange={(e) => setTransferForm(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full mt-1 border border-blue-200 rounded-md p-2 bg-white"
                  data-testid="transfer-reason-select"
                >
                  <option value="reabastecimiento">Reabastecimiento de stock</option>
                  <option value="solicitud">Solicitud de área</option>
                  <option value="emergencia">Transferencia de emergencia</option>
                  <option value="ajuste_stock">Ajuste entre áreas</option>
                  <option value="produccion">Para producción</option>
                  <option value="otro">Otro motivo</option>
                </select>
              </div>
              <div>
                <Label className="text-blue-700">Notas (opcional)</Label>
                <Input
                  value={transferForm.notes}
                  onChange={(e) => setTransferForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Agregar notas al traspaso..."
                  className="mt-1 border-blue-200"
                  data-testid="transfer-notes-input"
                />
              </div>
            </div>
          )}

          {/* Fecha del Vale */}
          <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
            <label className="block text-sm font-medium text-orange-800 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Fecha del Vale
            </label>
            <input
              type="date"
              value={valeFecha}
              onChange={(e) => setValeFecha(e.target.value)}
              className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="vale-fecha-input"
            />
          </div>

          <div className="flex gap-2 pt-4 border-t mt-4">
            <Button 
              type="button" 
              variant="outline" 
              className="flex-1"
              onClick={() => {
                setTransferModalOpen(false);
                setTransferForm({ from_area_id: '', to_area_id: '', supply_id: '', supply_search: '', quantity: '', reason: 'reabastecimiento', notes: '' });
                setTransferSourceStock([]);
                setTransferSourceProcessed([]);
                setTransferItems([]);
                setValeFecha(new Date().toISOString().slice(0, 10));
              }}
            >
              Cancelar
            </Button>
            <Button 
              type="button"
              variant="outline"
              className="flex-1 text-orange-600 border-orange-300 hover:bg-orange-50"
              onClick={handleDownloadValeSolicitud}
              disabled={!transferForm.from_area_id || !transferForm.to_area_id}
              data-testid="download-vale-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Vale Simple
            </Button>
            <Button 
              type="button"
              variant="outline"
              className="flex-1 text-indigo-600 border-indigo-300 hover:bg-indigo-50"
              onClick={handleDownloadValeCompleto}
              disabled={!transferForm.from_area_id || !transferForm.to_area_id}
              data-testid="download-vale-completo-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Vale + Checklist
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-orange-500 hover:bg-orange-600" 
              data-testid="confirm-transfer-btn"
              disabled={transferItems.length === 0 || savingTransfer}
            >
              {savingTransfer ? 'Guardando...' : `Realizar Traspaso (${transferItems.length})`}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Quick Adjustment Modal */}
      <Modal
        open={quickAdjustModalOpen}
        onClose={() => setQuickAdjustModalOpen(false)}
        title="Ajuste Rápido de Inventario"
      >
        {quickAdjustItem && (
          <form onSubmit={handleQuickAdjustSubmit} className="space-y-4">
            {/* Item Info */}
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="font-bold text-lg">{quickAdjustItem.supply_name}</p>
              <p className="text-sm text-slate-600">
                Stock actual: <span className="font-medium">{quickAdjustItem.quantity?.toFixed(2)} {quickAdjustItem.unit}</span>
              </p>
              {selectedArea && (
                <p className="text-sm text-slate-500">Área: {selectedArea.name}</p>
              )}
            </div>

            {/* Direction */}
            <div className="grid grid-cols-2 gap-2" data-testid="quick-adjust-direction">
              <button
                type="button"
                data-testid="quick-adjust-restar-btn"
                onClick={() => setQuickAdjustDirection('restar')}
                className={`p-2 rounded-lg border-2 font-bold transition-colors ${
                  quickAdjustForm.direction === 'restar' ? 'border-red-500 bg-red-50 text-red-600' : 'border-gray-200 text-slate-500 hover:border-gray-300'
                }`}
              >
                − Restar
              </button>
              <button
                type="button"
                data-testid="quick-adjust-sumar-btn"
                onClick={() => setQuickAdjustDirection('sumar')}
                className={`p-2 rounded-lg border-2 font-bold transition-colors ${
                  quickAdjustForm.direction === 'sumar' ? 'border-green-500 bg-green-50 text-green-600' : 'border-gray-200 text-slate-500 hover:border-gray-300'
                }`}
              >
                + Sumar
              </button>
            </div>

            {/* Adjustment Type */}
            <div className="space-y-2">
              <Label>Tipo de Ajuste *</Label>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_ADJUST_TYPES.filter(t => t.direction === 'ambos' || t.direction === quickAdjustForm.direction).map(type => (
                  <button
                    key={type.id}
                    type="button"
                    data-testid={`quick-adjust-type-${type.id}`}
                    onClick={() => setQuickAdjustForm({...quickAdjustForm, adjustment_type: type.id})}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      quickAdjustForm.adjustment_type === type.id 
                        ? (quickAdjustForm.direction === 'sumar' ? 'border-green-500 bg-green-50' : 'border-orange-500 bg-orange-50')
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className={`font-medium ${type.color}`}>{type.name}</span>
                    <p className="text-xs text-slate-500">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <Label>{quickAdjustForm.direction === 'sumar' ? 'Cantidad a sumar *' : 'Cantidad a restar *'}</Label>
              <div className="flex items-center gap-2">
                <span className={`font-bold text-xl ${quickAdjustForm.direction === 'sumar' ? 'text-green-600' : 'text-red-500'}`}>
                  {quickAdjustForm.direction === 'sumar' ? '+' : '−'}
                </span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={quickAdjustForm.direction === 'sumar' ? undefined : quickAdjustItem.quantity}
                  value={quickAdjustForm.quantity}
                  onChange={(e) => setQuickAdjustForm({...quickAdjustForm, quantity: e.target.value})}
                  placeholder="0.00"
                  required
                  className="flex-1"
                  data-testid="quick-adjust-quantity-input"
                />
                <span className="text-slate-500">{quickAdjustItem.unit}</span>
              </div>
              {quickAdjustForm.quantity && (
                <p className={`text-sm ${quickAdjustForm.direction === 'sumar' ? 'text-green-600' : 'text-red-500'}`} data-testid="quick-adjust-preview">
                  Stock resultante: {(Number(quickAdjustItem.quantity || 0) + (quickAdjustForm.direction === 'sumar' ? 1 : -1) * Math.abs(parseFloat(quickAdjustForm.quantity) || 0)).toFixed(2)} {quickAdjustItem.unit}
                  {quickAdjustItem.cost_per_unit > 0 && ` · ${quickAdjustForm.direction === 'sumar' ? 'Valor' : 'Costo de pérdida'}: $${(Math.abs(parseFloat(quickAdjustForm.quantity) || 0) * quickAdjustItem.cost_per_unit).toFixed(2)}`}
                </p>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Input
                value={quickAdjustForm.reason}
                onChange={(e) => setQuickAdjustForm({...quickAdjustForm, reason: e.target.value})}
                placeholder="Ej: Producto en mal estado, caducado, etc."
                required
              />
            </div>

            <Button 
              type="submit" 
              data-testid="quick-adjust-submit-btn"
              className={`w-full text-white ${quickAdjustForm.direction === 'sumar' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-500 hover:bg-orange-600'}`}
            >
              {quickAdjustForm.direction === 'sumar' ? 'Registrar Entrada' : 'Registrar Ajuste'}
            </Button>
          </form>
        )}
      </Modal>

      {/* Panel: Timeline de Traspasos */}
      {showTimelinePanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-orange-500 to-amber-500 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2"><History className="w-5 h-5"/>Timeline de Traspasos</h3>
              <button onClick={()=>setShowTimelinePanel(false)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {/* Gráfico de línea por día */}
              <div className="mb-4">
                <h4 className="text-sm font-bold text-gray-700 mb-2">Traspasos por Día</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={(() => {
                    const byDay = {};
                    transfers.forEach(t => {
                      const day = new Date(t.created_at).toLocaleDateString('es-MX', {day:'2-digit', month:'short'});
                      byDay[day] = (byDay[day] || 0) + 1;
                    });
                    return Object.entries(byDay).map(([day, count]) => ({ day, count })).slice(-14);
                  })()}>
                    <XAxis dataKey="day" tick={{fontSize:10}} />
                    <YAxis tick={{fontSize:10}} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#f97316" strokeWidth={2} dot={{fill:'#f97316'}} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Timeline visual */}
              <div className="space-y-2">
                {transfers.slice(0,20).map((t,i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg text-sm">
                    <div className="w-16 text-xs text-gray-500">{new Date(t.created_at).toLocaleDateString('es-MX',{day:'2-digit',month:'short'})}</div>
                    <div className="w-2 h-2 rounded-full bg-orange-500"/>
                    <div className="flex-1">
                      <span className="font-semibold">{t.supply_name}</span>
                      <span className="text-gray-500 mx-2">•</span>
                      <span className="text-blue-600">{t.from_area_name}</span>
                      <ArrowRight className="w-3 h-3 inline mx-1 text-gray-400"/>
                      <span className="text-purple-600">{t.to_area_name}</span>
                    </div>
                    <div className="font-bold text-green-600">${(t.total_cost||0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel: Valor por Día */}
      {showValuePanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-green-500 to-emerald-500 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2"><DollarSign className="w-5 h-5"/>Análisis de Valor</h3>
              <button onClick={()=>setShowValuePanel(false)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {/* Gráfico de barras por día */}
              <div className="mb-4">
                <h4 className="text-sm font-bold text-gray-700 mb-2">Valor por Día ($)</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={(() => {
                    const byDay = {};
                    transfers.forEach(t => {
                      const day = new Date(t.created_at).toLocaleDateString('es-MX', {day:'2-digit', month:'short'});
                      byDay[day] = (byDay[day] || 0) + (t.total_cost || 0);
                    });
                    return Object.entries(byDay).map(([day, value]) => ({ day, value: Math.round(value) })).slice(-14);
                  })()}>
                    <XAxis dataKey="day" tick={{fontSize:10}} />
                    <YAxis tick={{fontSize:10}} />
                    <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
                    <Bar dataKey="value" fill="#22c55e" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Comparativa */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">${transfers.reduce((s,t)=>s+(t.total_cost||0),0).toLocaleString('es-MX',{maximumFractionDigits:0})}</p>
                  <p className="text-xs text-gray-600">Total Período</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">${(transfers.reduce((s,t)=>s+(t.total_cost||0),0)/Math.max(transfers.length,1)).toFixed(2)}</p>
                  <p className="text-xs text-gray-600">Promedio por Traspaso</p>
                </div>
              </div>
              {/* Top costosos */}
              <h4 className="text-sm font-bold text-gray-700 mb-2">Traspasos Más Costosos</h4>
              <div className="space-y-1">
                {[...transfers].sort((a,b)=>(b.total_cost||0)-(a.total_cost||0)).slice(0,5).map((t,i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                    <span>{t.supply_name} ({t.from_area_name}→{t.to_area_name})</span>
                    <span className="font-bold text-green-600">${(t.total_cost||0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel: Detalle de Área */}
      {showAreaPanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className={`p-4 border-b text-white flex justify-between items-center ${showAreaPanel.type==='from'?'bg-gradient-to-r from-blue-500 to-cyan-500':'bg-gradient-to-r from-purple-500 to-pink-500'}`}>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Warehouse className="w-5 h-5"/>
                {showAreaPanel.type==='from'?'Envíos desde':'Recepciones en'}: {showAreaPanel.name}
              </h3>
              <button onClick={()=>setShowAreaPanel(null)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {/* Gráfico circular de destinos/orígenes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-2">{showAreaPanel.type==='from'?'¿A dónde envía?':'¿De dónde recibe?'}</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={(() => {
                          const filtered = transfers.filter(t => showAreaPanel.type==='from' ? t.from_area_name===showAreaPanel.name : t.to_area_name===showAreaPanel.name);
                          const counts = {};
                          filtered.forEach(t => {
                            const key = showAreaPanel.type==='from' ? t.to_area_name : t.from_area_name;
                            counts[key] = (counts[key] || 0) + 1;
                          });
                          return Object.entries(counts).map(([name, value]) => ({ name, value }));
                        })()}
                        cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`}
                      >
                        {(() => {
                          const filtered = transfers.filter(t => showAreaPanel.type==='from' ? t.from_area_name===showAreaPanel.name : t.to_area_name===showAreaPanel.name);
                          const counts = {};
                          filtered.forEach(t => {
                            const key = showAreaPanel.type==='from' ? t.to_area_name : t.from_area_name;
                            counts[key] = (counts[key] || 0) + 1;
                          });
                          return Object.keys(counts).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />);
                        })()}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-2">Insumos más movidos</h4>
                  <div className="space-y-1">
                    {(() => {
                      const filtered = transfers.filter(t => showAreaPanel.type==='from' ? t.from_area_name===showAreaPanel.name : t.to_area_name===showAreaPanel.name);
                      const counts = {};
                      filtered.forEach(t => { counts[t.supply_name] = (counts[t.supply_name] || 0) + t.quantity; });
                      return Object.entries(counts).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([name, qty], i) => (
                        <div key={i} className="flex justify-between p-1 bg-gray-50 rounded text-sm">
                          <span>{name}</span><span className="font-bold">{qty.toFixed(2)}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
              {/* Estadísticas */}
              <div className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xl font-bold text-orange-600">{transfers.filter(t => showAreaPanel.type==='from' ? t.from_area_name===showAreaPanel.name : t.to_area_name===showAreaPanel.name).length}</p>
                  <p className="text-xs text-gray-600">Traspasos</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-green-600">${transfers.filter(t => showAreaPanel.type==='from' ? t.from_area_name===showAreaPanel.name : t.to_area_name===showAreaPanel.name).reduce((s,t)=>s+(t.total_cost||0),0).toFixed(2)}</p>
                  <p className="text-xs text-gray-600">Valor Total</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-blue-600">{new Set(transfers.filter(t => showAreaPanel.type==='from' ? t.from_area_name===showAreaPanel.name : t.to_area_name===showAreaPanel.name).map(t=>t.supply_name)).size}</p>
                  <p className="text-xs text-gray-600">Insumos Únicos</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel: Detalle de Insumo */}
      {showSupplyPanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-amber-500 to-orange-500 text-white flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2"><Package className="w-5 h-5"/>Historial: {showSupplyPanel}</h3>
              <button onClick={()=>setShowSupplyPanel(null)} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {/* Gráfico de movimiento */}
              <div className="mb-4">
                <h4 className="text-sm font-bold text-gray-700 mb-2">Movimiento en el tiempo</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={(() => {
                    const filtered = transfers.filter(t => (t.supply_name || t.supply_id) === showSupplyPanel);
                    const byDay = {};
                    filtered.forEach(t => {
                      const day = new Date(t.created_at).toLocaleDateString('es-MX', {day:'2-digit', month:'short'});
                      byDay[day] = (byDay[day] || 0) + t.quantity;
                    });
                    return Object.entries(byDay).map(([day, qty]) => ({ day, qty }));
                  })()}>
                    <XAxis dataKey="day" tick={{fontSize:10}} />
                    <YAxis tick={{fontSize:10}} />
                    <Tooltip />
                    <Bar dataKey="qty" fill="#f97316" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Flujo */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <h5 className="text-xs font-bold text-blue-700 mb-2">Sale de:</h5>
                  {(() => {
                    const filtered = transfers.filter(t => (t.supply_name || t.supply_id) === showSupplyPanel);
                    const counts = {};
                    filtered.forEach(t => { 
                      const loc = getTransferLocationInfo(t);
                      const key = loc.fromFull;
                      counts[key] = (counts[key] || 0) + t.quantity; 
                    });
                    return Object.entries(counts).sort((a,b)=>b[1]-a[1]).map(([name, qty], i) => (
                      <div key={i} className="flex justify-between text-sm"><span>{name}</span><span className="font-bold">{qty.toFixed(2)}</span></div>
                    ));
                  })()}
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <h5 className="text-xs font-bold text-purple-700 mb-2">Llega a:</h5>
                  {(() => {
                    const filtered = transfers.filter(t => (t.supply_name || t.supply_id) === showSupplyPanel);
                    const counts = {};
                    filtered.forEach(t => { 
                      const loc = getTransferLocationInfo(t);
                      const key = loc.toFull;
                      counts[key] = (counts[key] || 0) + t.quantity; 
                    });
                    return Object.entries(counts).sort((a,b)=>b[1]-a[1]).map(([name, qty], i) => (
                      <div key={i} className="flex justify-between text-sm"><span>{name}</span><span className="font-bold">{qty.toFixed(2)}</span></div>
                    ));
                  })()}
                </div>
              </div>
              {/* Historial */}
              <h4 className="text-sm font-bold text-gray-700 mb-2">Últimos movimientos</h4>
              <div className="space-y-1">
                {transfers.filter(t => (t.supply_name || t.supply_id) === showSupplyPanel).slice(0,10).map((t, i) => {
                  const loc = getTransferLocationInfo(t);
                  return (
                    <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                      <span className="text-gray-500 w-20">{new Date(t.created_at).toLocaleDateString('es-MX',{day:'2-digit',month:'short'})}</span>
                      <span className="flex-1">{loc.fromFull} → {loc.toFull}</span>
                      <span className="font-bold">{t.quantity} {t.unit}</span>
                      <span className="text-green-600 font-bold w-20 text-right">${(t.total_cost||0).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel: Dashboard Completo de Productos */}
      {showProductsDashboard && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-amber-500 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Package className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Dashboard de Productos Trasladados</h2>
                    <p className="text-orange-100 text-sm">Análisis completo de movimientos por producto</p>
                  </div>
                </div>
                <button 
                  onClick={() => {setShowProductsDashboard(false); setProductsDashboardCategory(null);}}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-5">
              {(() => {
                // Calcular estadísticas de productos
                const productStats = {};
                transfers.forEach(t => {
                  const name = t.supply_name || t.supply_id;
                  if (!productStats[name]) {
                    productStats[name] = {
                      name,
                      quantity: 0,
                      cost: 0,
                      movements: 0,
                      unit: t.unit || 'pz',
                      fromAreas: new Set(),
                      toAreas: new Set(),
                      lastMove: t.created_at
                    };
                  }
                  productStats[name].quantity += t.quantity;
                  productStats[name].cost += t.total_cost || 0;
                  productStats[name].movements += 1;
                  productStats[name].fromAreas.add(t.from_area_name);
                  productStats[name].toAreas.add(t.to_area_name);
                  if (t.created_at > productStats[name].lastMove) {
                    productStats[name].lastMove = t.created_at;
                  }
                });
                
                const allProducts = Object.values(productStats).sort((a, b) => b.quantity - a.quantity);
                const totalProducts = allProducts.length;
                const totalValue = allProducts.reduce((s, p) => s + p.cost, 0);
                const totalMovements = allProducts.reduce((s, p) => s + p.movements, 0);
                const topProduct = allProducts[0];
                
                // Agrupar por área de destino
                const byDestination = {};
                transfers.forEach(t => {
                  const area = t.to_area_name;
                  if (!byDestination[area]) byDestination[area] = {};
                  const name = t.supply_name || t.supply_id;
                  if (!byDestination[area][name]) {
                    byDestination[area][name] = { name, quantity: 0, cost: 0, movements: 0, unit: t.unit || 'pz' };
                  }
                  byDestination[area][name].quantity += t.quantity;
                  byDestination[area][name].cost += t.total_cost || 0;
                  byDestination[area][name].movements += 1;
                });
                
                return (
                  <>
                    {/* Resumen Principal */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                        <div className="flex items-center justify-between mb-2">
                          <Package className="w-8 h-8 opacity-80" />
                          <span className="text-3xl font-bold">{totalProducts}</span>
                        </div>
                        <p className="text-blue-100 text-sm">Productos Diferentes</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                        <div className="flex items-center justify-between mb-2">
                          <DollarSign className="w-8 h-8 opacity-80" />
                          <span className="text-2xl font-bold">${totalValue.toLocaleString('es-MX', {minimumFractionDigits: 0, maximumFractionDigits: 0})}</span>
                        </div>
                        <p className="text-green-100 text-sm">Valor Total Movido</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                        <div className="flex items-center justify-between mb-2">
                          <ArrowRightLeft className="w-8 h-8 opacity-80" />
                          <span className="text-3xl font-bold">{totalMovements}</span>
                        </div>
                        <p className="text-purple-100 text-sm">Total Movimientos</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
                        <div className="flex items-center justify-between mb-2">
                          <TrendUp className="w-8 h-8 opacity-80" />
                          <span className="text-lg font-bold truncate">{topProduct?.name || '-'}</span>
                        </div>
                        <p className="text-orange-100 text-sm">Más Trasladado ({topProduct?.quantity.toFixed(1) || 0})</p>
                      </div>
                    </div>
                    
                    {/* Top 10 con Gráfico de Barras */}
                    <div className="bg-slate-50 rounded-xl p-4 mb-6">
                      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-orange-500" />
                        Top 10 Productos por Cantidad
                      </h3>
                      <div className="space-y-2">
                        {allProducts.slice(0, 10).map((product, idx) => {
                          const maxQty = allProducts[0]?.quantity || 1;
                          const percentage = (product.quantity / maxQty) * 100;
                          return (
                            <div 
                              key={product.name}
                              className="bg-white rounded-lg p-3 cursor-pointer hover:shadow-md transition-all border hover:border-orange-300"
                              onClick={() => {setShowProductsDashboard(false); setShowSupplyPanel(product.name);}}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold ${idx < 3 ? 'bg-orange-500' : 'bg-slate-400'}`}>
                                    {idx + 1}
                                  </span>
                                  <span className="font-medium text-gray-800">{product.name}</span>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="text-gray-500">{product.movements} mov.</span>
                                  <span className="font-bold text-green-600">${product.cost.toFixed(0)}</span>
                                  <span className="font-bold text-orange-600 min-w-[80px] text-right">{product.quantity.toFixed(2)} {product.unit}</span>
                                </div>
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${idx < 3 ? 'bg-gradient-to-r from-orange-400 to-orange-500' : 'bg-gradient-to-r from-slate-300 to-slate-400'}`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Productos por Área de Destino */}
                    <div className="bg-white rounded-xl border">
                      <h3 className="font-bold text-gray-800 p-4 border-b flex items-center gap-2">
                        <Warehouse className="w-5 h-5 text-blue-500" />
                        Productos por Área de Destino
                        <span className="text-xs text-gray-400 font-normal ml-2">(Clic para expandir)</span>
                      </h3>
                      <div className="divide-y">
                        {Object.entries(byDestination).map(([area, products]) => {
                          const productList = Object.values(products).sort((a, b) => b.quantity - a.quantity);
                          const areaTotal = productList.reduce((s, p) => s + p.cost, 0);
                          const isExpanded = productsDashboardCategory === area;
                          
                          return (
                            <div key={area}>
                              <div 
                                className={`p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${isExpanded ? 'bg-blue-50' : ''}`}
                                onClick={() => setProductsDashboardCategory(isExpanded ? null : area)}
                              >
                                <div className="flex items-center gap-3">
                                  <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                  <div className="p-2 bg-blue-100 rounded-lg">
                                    {area === 'Cocina' ? <ChefHat className="w-5 h-5 text-blue-600" /> : 
                                     area === 'Barra' ? <Wine className="w-5 h-5 text-blue-600" /> :
                                     <Warehouse className="w-5 h-5 text-blue-600" />}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-800">{area}</p>
                                    <p className="text-xs text-gray-500">{productList.length} productos diferentes</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-green-600">${areaTotal.toLocaleString('es-MX', {minimumFractionDigits: 0})}</p>
                                  <p className="text-xs text-gray-500">valor recibido</p>
                                </div>
                              </div>
                              
                              {/* Lista expandida de productos */}
                              {isExpanded && (
                                <div className="bg-slate-50 p-4 border-t">
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {productList.map((product, idx) => (
                                      <div 
                                        key={product.name}
                                        className="bg-white rounded-lg p-3 border hover:border-orange-300 hover:shadow cursor-pointer transition-all"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowProductsDashboard(false);
                                          setShowSupplyPanel(product.name);
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <span className="font-medium text-gray-800 truncate flex-1">{product.name}</span>
                                          <span className="text-orange-600 font-bold ml-2">{product.quantity.toFixed(1)}</span>
                                        </div>
                                        <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                                          <span>{product.movements} movimientos</span>
                                          <span className="text-green-600 font-medium">${product.cost.toFixed(0)}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Lista completa de todos los productos */}
                    <div className="mt-6 bg-white rounded-xl border">
                      <h3 className="font-bold text-gray-800 p-4 border-b flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <ClipboardList className="w-5 h-5 text-purple-500" />
                          Todos los Productos ({totalProducts})
                        </span>
                      </h3>
                      <div className="max-h-[300px] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="text-left p-3 font-semibold text-gray-600">#</th>
                              <th className="text-left p-3 font-semibold text-gray-600">Producto</th>
                              <th className="text-right p-3 font-semibold text-gray-600">Cantidad</th>
                              <th className="text-right p-3 font-semibold text-gray-600">Movimientos</th>
                              <th className="text-right p-3 font-semibold text-gray-600">Costo Total</th>
                              <th className="text-center p-3 font-semibold text-gray-600">Acción</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {allProducts.map((product, idx) => (
                              <tr key={product.name} className="hover:bg-orange-50 transition-colors">
                                <td className="p-3 text-gray-500">{idx + 1}</td>
                                <td className="p-3 font-medium text-gray-800">{product.name}</td>
                                <td className="p-3 text-right font-semibold text-orange-600">{product.quantity.toFixed(2)} {product.unit}</td>
                                <td className="p-3 text-right text-gray-600">{product.movements}</td>
                                <td className="p-3 text-right font-semibold text-green-600">${product.cost.toFixed(2)}</td>
                                <td className="p-3 text-center">
                                  <button
                                    onClick={() => {setShowProductsDashboard(false); setShowSupplyPanel(product.name);}}
                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 px-2 py-1 rounded text-xs font-medium"
                                  >
                                    Ver Detalle
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
